// @ts-nocheck
/**
 * @file Diagram canvas handled by the Cytoscape.js library.
 * 新交互模式：
 * - 点击节点文字区域：选中节点，显示8个缩放控制点和操作菜单
 * - 点击节点非文字区域：进入连线模式
 * - 鼠标移入节点：显示十字光标（表示可连线）
 * - 鼠标移入文字区域：显示移动光标（表示可拖动）
 * - 右键拖动：框选
 */

import cytoscape from '../dependencies/cytoscape.js/build/cytoscape.esm.js';
import type { Core, EdgeSingular, NodeSingular, Position } from 'cytoscape';
import 'cytoscape-context-menus/cytoscape-context-menus.css';
import coseBilkent from 'cytoscape-cose-bilkent';
import edgehandles from 'cytoscape-edgehandles';
import popper from 'cytoscape-popper';
import { createPopper } from '@popperjs/core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { message, Modal } from 'antd';
import {
  edgeCreate,
  edgeCreateDragOutOfElement,
  edgeCreateDragOverElement,
  edgeCreateStart,
  edgeCreateStop,
  edgeCreateValidate,
  edgeCreateCancel,
  getEdgeCreateDirectionInfo,
} from '../controller/edge';
import {
  InteractionMode,
  isPointInLabelArea,
  isPointInDraggableArea,
  clearLabelBBoxCache,
  getHitResizeHandle,
  getResizeCursor,
} from '../controller/node-interaction';
import ResizeHandles from './ResizeHandles';
import NodeActionMenu, {
  createDefaultMenuItems,
  MenuItem,
} from './NodeActionMenu';
import ImportDataDialog from '../../OPMEditor/ImportDataDialog';
import ProcessFunctionDialog from './ProcessDefinition';
import BoxSelection from './BoxSelection';
import { useSearchParams } from 'react-router-dom';
import EdgeEditDialog from './EdgeEditDialog';
import {
  addEdge,
  addInzoomedNodes,
  addNodeFromContextMenu,
  bringAllStates,
  clampChildNodeWithinParentTitleSafeArea,
  clampCompoundChildrenAwayFromTitle,
  createCytoEdgeData,
  detachDiagramSubtree,
  removeEdgeContextMenu,
  removeNodeContextMenu,
  relayoutStateValueChildren,
  resolveDiagramTopLevelParent,
} from '../controller/general';
import '../css/general.css';
import { DiagramTreeNode, diagramTreeRoot } from '../model/diagram-tree-model';
import {
  Affiliation,
  Essence,
  ChildGroupType,
  MMNode,
  MMRoot,
  NodeType,
  masterModelRoot,
} from '../model/node-model';
import {
  EdgeType,
  hierarchicalStructuralEdges,
  MMEdge,
  originalEdgeArray,
  LinkQualifier,
  LinkNegation,
} from '../model/edge-model';
import {
  getAllowedEdgeTypesByMMNodes,
  getInvalidEdgeMessageByMMNodes,
  getInvalidStateAdditionMessage,
} from '../utils/opl-rules';
import { eeDefaults } from '../options/cytoscape-edge-editing-defaults';
import { ehDefaults } from '../options/cytoscape-edge-handles-defaults';
import { cyStylesheet } from '../options/cytoscape-stylesheet';
import registerCustomArrowShapes from '../options/registerCustomArrowShapes';
import {
  ACTIONS,
  shouldSkipDiagramRenderTracking,
  PropagationEnum,
  useReducerProps,
  isInitialLoading,
} from './App';
import FloatingToolbar from './FloatingToolbar';
import {
  resetSubdiagramViewOnInitialOpen,
  runWithDiagramSwitching,
  switchDiagrams,
  syncSubdiagramVisualStateFromMM,
  updateFromMasterModel,
} from '../controller/diagram-switching';
import ExecutionOverlay from '../simulation/overlay/ExecutionOverlay';
import {
  computeDisplayLabel,
  generateUniqueCodeNameByLabel,
  generateUniqueSequencedValue,
  buildUsedNodeLabelSet,
  buildUsedNodeCodeNameSet,
  refreshStructuralEdgeStyles,
  smartFitView,
} from '../controller/general';
import { eleCounter } from '../controller/elementCounter';
import { applyAutoLabelWidth } from '../utils/node-label-size';
import { convertOPMEditorToPlain } from '../utils/opm-data-adapter';
import { recalculateNamespacesForMasterModel } from '../utils/node-namespace';
import { processImportedData } from '../../OPMEditor/utils/importDataHelpers';
import { OPMApi } from '../../../api/opmApi';
import {
  buildMarkerLabel,
  getAllowedNegations,
  getEdgeMarkerOption,
} from '../options/link-marker-config';
import { useConfig } from '../../../hooks/useConfig';

import $ from 'jquery';
import Konva from 'konva';
import edgeEditing from 'cytoscape-edge-editing';
import { addNode } from '../controller/general';

if (typeof window !== 'undefined') {
  (window as any).$ = $;
}

const opmcvDebugEventsEnabled =
  typeof window !== 'undefined' && !!(window as any).__OPMCV_DEBUG_EVENTS;
const isOpmcvDragDiagEnabled = () =>
  typeof window !== 'undefined' && !!(window as any).__OPMCV_DRAG_DIAG;
const opmcvDragDiagNow = () =>
  typeof performance !== 'undefined' && performance.now
    ? performance.now()
    : Date.now();
const opmcvDragDiagLog = (...args: any[]) => {
  if (!isOpmcvDragDiagEnabled()) return;
  console.log('[OPMCV-DRAG]', ...args);
};

// 扩展注册（移除 contextMenus，用悬浮菜单替代）
if (!(cytoscape as any).prototype.edgeEditing) {
  edgeEditing(cytoscape, $, Konva);
}
cytoscape.use(edgehandles);
cytoscape.use(coseBilkent);
cytoscape.use(popper(createPopper));
cytoscape.use(coseBilkent);

// 模块级变量供外部文件导入使用
let cy: Core;

const MARKER_LABEL_NODE_CLASS = 'marker-label';
const MARKER_LABEL_NODE_PREFIX = 'marker-label-';
const MARKER_LABEL_OFFSET = {
  along: -26,
  normal: -18,
};
const PARENT_CHILD_INVOCATION_OVERLAY_MARKER_ID =
  'opmcv-parent-child-invocation-arrow';
const parentChildInvocationOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  zIndex: 1001,
  overflow: 'visible',
};
const INVOCATION_ZIGZAG_HALF = 20;
const INVOCATION_ZIGZAG_OFFSET = 6;
const PARENT_CHILD_INVOCATION_TARGET_INSET = 8;
const PARENT_CHILD_INVOCATION_WIDTH_BOOST = 0.4;
const EDGE_SELECTION_DOT_RADIUS = 5;
const INFLUENCE_ARROW_POINTS = [
  -0.033, -0.238, -0.091, -0.3, 0, 0, 0.091, -0.3, 0.033, -0.238, 0, -0.203,
];

const getMarkerLabelNodeId = (edgeId: string) =>
  `${MARKER_LABEL_NODE_PREFIX}${edgeId}`;

const toScreenPoint = (cy: Core, x: number, y: number) => {
  const pan = cy.pan();
  const zoom = cy.zoom();
  return {
    x: x * zoom + pan.x,
    y: y * zoom + pan.y,
  };
};

const getEllipseBoundaryPoint = (
  centerX: number,
  centerY: number,
  radiusX: number,
  radiusY: number,
  targetX: number,
  targetY: number
) => {
  const dx = targetX - centerX;
  const dy = targetY - centerY;
  if (!dx && !dy) {
    return { x: centerX + radiusX, y: centerY };
  }
  const scale =
    1 /
    Math.sqrt(
      (dx * dx) / (radiusX * radiusX) + (dy * dy) / (radiusY * radiusY)
    );
  return {
    x: centerX + dx * scale,
    y: centerY + dy * scale,
  };
};

const retreatPoint = (
  start: { x: number; y: number },
  end: { x: number; y: number },
  distance: number
) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (!length || distance <= 0) return end;
  const ratio = Math.max(0, (length - distance) / length);
  return {
    x: start.x + dx * ratio,
    y: start.y + dy * ratio,
  };
};

const buildInvocationOverlayPath = (
  start: { x: number; y: number },
  end: { x: number; y: number }
) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (!length) {
    return `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
  }
  const ux = dx / length;
  const uy = dy / length;
  const nx = -uy;
  const ny = ux;
  const middleX = (start.x + end.x) / 2;
  const middleY = (start.y + end.y) / 2;
  const point1 = {
    x: middleX + ux * INVOCATION_ZIGZAG_HALF - nx * INVOCATION_ZIGZAG_OFFSET,
    y: middleY + uy * INVOCATION_ZIGZAG_HALF - ny * INVOCATION_ZIGZAG_OFFSET,
  };
  const point2 = {
    x: middleX - ux * INVOCATION_ZIGZAG_HALF + nx * INVOCATION_ZIGZAG_OFFSET,
    y: middleY - uy * INVOCATION_ZIGZAG_HALF + ny * INVOCATION_ZIGZAG_OFFSET,
  };
  return `M ${start.x} ${start.y} L ${point1.x} ${point1.y} L ${point2.x} ${point2.y} L ${end.x} ${end.y}`;
};

const getInfluenceArrowMarkerPath = () => {
  const size = 72;
  const translation = { x: 22, y: 12 };
  return (
    INFLUENCE_ARROW_POINTS.reduce((result, value, index, points) => {
      if (index % 2 !== 0) return result;
      const x = points[index];
      const y = points[index + 1];
      const markerX = translation.x + y * size;
      const markerY = translation.y - x * size;
      return `${result}${index === 0 ? 'M' : ' L'} ${markerX} ${markerY}`;
    }, '') + ' Z'
  );
};

const buildParentChildInvocationOverlayLines = (cy: Core) => {
  const lines: Array<{
    id: string;
    d: string;
    stroke: string;
    strokeWidth: number;
    start: { x: number; y: number };
    end: { x: number; y: number };
  }> = [];
  cy.edges().forEach((edge: any) => {
    if (edge.data('display') === 'none') return;
    const mmRef = edge.data('MMRef') as MMEdge | null;
    if (!mmRef || mmRef.type !== EdgeType.Invocation) return;
    if (mmRef.source.parent !== mmRef.target) return;

    const childNode = cy.getElementById(mmRef.source.id);
    const parentNode = cy.getElementById(mmRef.target.id);
    if (!childNode || childNode.empty() || !parentNode || parentNode.empty()) {
      return;
    }

    const childBb = childNode.boundingBox({ includeLabels: false });
    const parentBb = parentNode.boundingBox({ includeLabels: false });
    const childCenterX = (childBb.x1 + childBb.x2) / 2;
    const childCenterY = (childBb.y1 + childBb.y2) / 2;
    const parentCenterX = (parentBb.x1 + parentBb.x2) / 2;
    const parentCenterY = (parentBb.y1 + parentBb.y2) / 2;
    const horizontalDirection = childCenterX >= parentCenterX ? 1 : -1;
    const verticalDirection = childCenterY >= parentCenterY ? 1 : -1;
    const childRadiusX = Math.max(1, (childBb.x2 - childBb.x1) / 2);
    const childRadiusY = Math.max(1, (childBb.y2 - childBb.y1) / 2);
    const parentRadiusX = Math.max(1, (parentBb.x2 - parentBb.x1) / 2);
    const parentRadiusY = Math.max(1, (parentBb.y2 - parentBb.y1) / 2);
    const arrowAimX =
      parentCenterX + horizontalDirection * (parentRadiusX + 42);
    const arrowAimY =
      childCenterY +
      verticalDirection *
        Math.max(18, Math.abs(childCenterY - parentCenterY) * 0.2);
    const childBoundary = getEllipseBoundaryPoint(
      childCenterX,
      childCenterY,
      childRadiusX,
      childRadiusY,
      arrowAimX,
      arrowAimY
    );
    const parentBoundary = getEllipseBoundaryPoint(
      parentCenterX,
      parentCenterY,
      parentRadiusX,
      parentRadiusY,
      arrowAimX,
      arrowAimY
    );

    const start = toScreenPoint(cy, childBoundary.x, childBoundary.y);
    const boundaryEnd = toScreenPoint(cy, parentBoundary.x, parentBoundary.y);
    const end = retreatPoint(
      start,
      boundaryEnd,
      PARENT_CHILD_INVOCATION_TARGET_INSET
    );
    const d = buildInvocationOverlayPath(start, end);
    const stroke = String(edge.style('line-color') || '#586D8C');
    const widthValue = edge.style('width');
    const strokeWidth = Math.max(
      1,
      typeof widthValue === 'number'
        ? widthValue + PARENT_CHILD_INVOCATION_WIDTH_BOOST
        : (parseFloat(String(widthValue || '2')) || 2) +
            PARENT_CHILD_INVOCATION_WIDTH_BOOST
    );
    lines.push({ id: edge.id(), d, stroke, strokeWidth, start, end });
  });
  return lines;
};

const buildSelectedEdgeReadonlyHandles = (instance: Core) => {
  const selected = instance.$('edge:selected');
  if (!selected || selected.length === 0) return [];

  return selected
    .toArray()
    .map((edge: any) => {
      const mmRef = edge.data('MMRef') as MMEdge | null;
      if (
        mmRef?.type === EdgeType.Invocation &&
        mmRef.source.parent === mmRef.target
      ) {
        const overlayLine = buildParentChildInvocationOverlayLines(
          instance
        ).find(line => line.id === edge.id());
        if (!overlayLine) return null;
        return {
          id: edge.id(),
          source: overlayLine.start,
          target: overlayLine.end,
        };
      }

      const source = edge.sourceEndpoint?.();
      const target = edge.targetEndpoint?.();
      if (!source || !target) return null;
      return {
        id: edge.id(),
        source: toScreenPoint(instance, source.x, source.y),
        target: toScreenPoint(instance, target.x, target.y),
      };
    })
    .filter(Boolean);
};

const getMarkerLabelPosition = (edge: EdgeSingular): Position | null => {
  // 已从图中移除的边无法获取端点坐标，直接返回 null
  if (!edge || edge.empty?.() || edge.removed?.()) return null;
  const sourcePos = edge.sourceEndpoint();
  const targetPos = edge.targetEndpoint();
  const dx = targetPos.x - sourcePos.x;
  const dy = targetPos.y - sourcePos.y;
  const length = Math.hypot(dx, dy);
  if (!length) return targetPos;
  const ux = dx / length;
  const uy = dy / length;
  const nx = -uy;
  const ny = ux;
  return {
    x:
      targetPos.x +
      ux * MARKER_LABEL_OFFSET.along +
      nx * MARKER_LABEL_OFFSET.normal,
    y:
      targetPos.y +
      uy * MARKER_LABEL_OFFSET.along +
      ny * MARKER_LABEL_OFFSET.normal,
  };
};

const syncMarkerLabelNode = (cy: Core, edge: EdgeSingular) => {
  // 边已从图中删除时跳过同步，避免 requestAnimationFrame 异步回调在 remove 后重建标签节点
  if (!edge || edge.removed?.()) return;
  const rawMarkerLabel = edge?.data?.('markerLabel');
  const markerLabel =
    typeof rawMarkerLabel === 'string' ? rawMarkerLabel.trim() : '';
  const labelId = getMarkerLabelNodeId(edge.id());
  const existing = cy.getElementById(labelId);

  if (!markerLabel) {
    if (existing.length > 0) {
      existing.remove();
    }
    return;
  }

  const position = getMarkerLabelPosition(edge);
  if (!position) return;
  const display = edge.data('display') === 'none' ? 'none' : 'element';
  const markerColor = edge.data('markerColor') || '#586D8C';

  if (existing.length > 0) {
    // 小标节点不能锁定，否则位置更新不生效
    if (existing.locked && existing.locked()) {
      existing.unlock();
    }
    existing.data({
      markerLabel,
      markerColor,
      display,
      isMarkerLabel: true,
      markerLabelEdgeId: edge.id(),
    });
    existing.position(position);
    return;
  }

  cy.add({
    group: 'nodes',
    data: {
      id: labelId,
      markerLabel,
      markerColor,
      display,
      isMarkerLabel: true,
      markerLabelEdgeId: edge.id(),
    },
    position,
    selectable: false,
    grabbable: false,
    classes: MARKER_LABEL_NODE_CLASS,
  });
};

const syncMarkerLabelNodes = (cy: Core, edges: any) => {
  // 批量同步连线小标节点，降低频繁更新时的渲染抖动
  cy.batch(() => {
    edges.forEach((edge: EdgeSingular) => {
      syncMarkerLabelNode(cy, edge);
    });
  });
};

const removeMarkerLabelNode = (cy: Core, edge: EdgeSingular) => {
  const labelId = getMarkerLabelNodeId(edge.id());
  const labelNode = cy.getElementById(labelId);
  if (labelNode.length > 0) {
    labelNode.remove();
  }
};

// 剪贴板数据结构（跨图表复制粘贴）
interface ClipboardNode {
  id: string;
  label: string;
  type: string;
  parent: string; // 原父节点ID
  position: { x: number; y: number };
  data: any; // 完整的节点数据
}
interface ClipboardEdge {
  id: string;
  label: string;
  type: string;
  source: string;
  target: string;
  data: any;
  linkQualifier?: LinkQualifier;
  linkNegation?: LinkNegation;
  markerColor?: string;
}
interface Clipboard {
  nodes: ClipboardNode[];
  edges: ClipboardEdge[];
  centerX: number; // 复制时的中心点X
  centerY: number; // 复制时的中心点Y
}
let clipboard: Clipboard | null = null;

interface InzoomNodeSnapshot {
  id: string;
  position: { x: number; y: number };
  data: any;
}

interface InzoomEdgeSnapshot {
  id: string;
  MMRef: MMEdge | null;
}

interface InzoomSnapshot {
  rootId: string;
  nodes: InzoomNodeSnapshot[];
  edges: InzoomEdgeSnapshot[];
}

const DiagramCanvas: React.FC<useReducerProps> = ({
  state,
  dispatch,
  onNodeSelect,
  onEdgeSelect,
  onDataMap,
  isDataMapNavigating,
  onRenderStart,
  onRenderEnd,
  simulationSnapshot,
}) => {
  // 从URL查询参数获取ontologyId (例如: ?id=2)
  const [searchParams] = useSearchParams();
  const ontologyIdParam = searchParams.get('id');
  const ontologyId = ontologyIdParam ? parseInt(ontologyIdParam, 10) : 1;

  //workaround
  const currentDiagram = useRef<DiagramTreeNode>();
  currentDiagram.current = state.currentDiagram;
  const propagation = useRef<PropagationEnum>();
  propagation.current = state.propagation;
  const diagramContainerRef = useRef<HTMLDivElement | null>(null);
  const cyContainerRef = useRef<HTMLDivElement | null>(null);

  // 使用 state 管理 cy 初始化状态，确保初始化后触发重新渲染，让 BoxSelection 能正确绑定事件
  const [cyInstance, setCyInstance] = useState<Core | null>(null);

  // 新交互状态
  const [interactionMode, setInteractionMode] = useState<InteractionMode>(
    InteractionMode.None
  );
  const [selectedNode, setSelectedNode] = useState<NodeSingular | null>(null);
  const [isLabelSelected, setIsLabelSelected] = useState(false); // 是否通过点击文字选中
  const [boxSelectedNodes, setBoxSelectedNodes] = useState<NodeSingular[]>([]);
  const [boxSelectionBounds, setBoxSelectionBounds] = useState<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } | null>(null); // 框选边界
  const [clearSelectionTrigger, setClearSelectionTrigger] = useState(0);
  const [showImportDataDialog, setShowImportDataDialog] = useState(false);
  const [importingNode, setImportingNode] = useState<NodeSingular | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [showFunctionDialog, setShowFunctionDialog] = useState(false);
  const [functionNode, setFunctionNode] = useState<NodeSingular | null>(null);
  const [
    parentChildInvocationOverlayVersion,
    setParentChildInvocationOverlayVersion,
  ] = useState(0);
  const [edgeDialogMode, setEdgeDialogMode] = useState<'create' | 'edit'>(
    'edit'
  );
  const [showEdgeEditDialog, setShowEdgeEditDialog] = useState(false);
  const [editingEdge, setEditingEdge] = useState<EdgeSingular | null>(null);
  const [editingEdgeType, setEditingEdgeType] = useState<EdgeType | null>(null);
  const [editingEdgeLabel, setEditingEdgeLabel] = useState('');
  const [editingEdgeQualifier, setEditingEdgeQualifier] =
    useState<LinkQualifier>(LinkQualifier.None);
  const [editingEdgeNegation, setEditingEdgeNegation] = useState<LinkNegation>(
    LinkNegation.None
  );
  const [edgeDirection, setEdgeDirection] = useState<{
    sourceId?: string;
    targetId?: string;
    sourceLabel: string;
    targetLabel: string;
    sourceType?: string;
    targetType?: string;
    sourceMMRef?: MMNode | null;
    targetMMRef?: MMNode | null;
  } | null>(null);
  const getMarkerColorByEdge = useCallback((edgeRef?: MMEdge | null) => {
    if (!edgeRef) return '#586D8C';
    return edgeRef.originalEdges?.length ? '#ccc' : '#586D8C';
  }, []);
  const isConnectingRef = useRef(false); // 是否正在连线
  const ehRef = useRef<any>(null); // edgehandles 实例
  const importingNodeRef = useRef<NodeSingular | null>(null);

  // 用 ref 存储状态，避免闭包问题
  const selectedNodeRef = useRef<NodeSingular | null>(null);
  const isLabelSelectedRef = useRef(false);
  const boxSelectedNodesRef = useRef<NodeSingular[]>([]);
  const boxSelectionBoundsRef = useRef<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  } | null>(null);
  const dragStartedRef = useRef(false);
  const dragEndedRef = useRef(false);
  const dragMovedRef = useRef(false);
  const activeDraggedNodeIdRef = useRef<string | null>(null);
  const tapStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const { globalConfig } = useConfig();
  const showFunctionDefinitionMenu = !!(globalConfig as any)
    .showFunctionDefinitionMenu;

  // 同步状态到 ref
  useEffect(() => {
    selectedNodeRef.current = selectedNode;
  }, [selectedNode]);
  useEffect(() => {
    isLabelSelectedRef.current = isLabelSelected;
  }, [isLabelSelected]);

  useEffect(() => {
    const instance = cyInstance || cy;
    if (!instance) return;

    let rafId: number | null = null;
    const scheduleOverlayUpdate = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        setParentChildInvocationOverlayVersion(version => version + 1);
      });
    };

    instance.on(
      'pan zoom resize render position add remove data',
      scheduleOverlayUpdate
    );
    scheduleOverlayUpdate();

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      instance.off(
        'pan zoom resize render position add remove data',
        scheduleOverlayUpdate
      );
    };
  }, [cyInstance]);

  const parentChildInvocationOverlayLines = useMemo(() => {
    const instance = cyInstance || cy;
    if (!instance) return [];
    return buildParentChildInvocationOverlayLines(instance);
  }, [cyInstance, parentChildInvocationOverlayVersion]);

  const selectedEdgeReadonlyHandles = useMemo(() => {
    const instance = cyInstance || cy;
    if (!instance) return [];
    return buildSelectedEdgeReadonlyHandles(instance);
  }, [cyInstance, parentChildInvocationOverlayVersion]);

  const selectEdgeFromOverlay = useCallback(
    (edgeId: string) => {
      const instance = cyInstance || cy;
      if (!instance || !edgeId) return;
      if (
        typeof window !== 'undefined' &&
        (window as any).__opmcvNodeEditorBlockClearSelection
      ) {
        return;
      }
      const edge = instance.getElementById(edgeId) as EdgeSingular;
      if (!edge || edge.empty?.()) return;
      setSelectedNode(null);
      setIsLabelSelected(false);
      setInteractionMode(InteractionMode.None);
      onNodeSelect?.(null);
      onEdgeSelect?.(edge);
      instance.elements().unselect();
      edge.select();
    },
    [cyInstance, onEdgeSelect, onNodeSelect]
  );

  // 对外暴露清理当前文字选中态的能力，供正式弹窗打开时关闭浮动菜单。
  useEffect(() => {
    (window as any).__opmcvClearNodeSelection = () => {
      if (!cy) return;
      setSelectedNode(null);
      setIsLabelSelected(false);
      setInteractionMode(InteractionMode.None);
      onNodeSelect?.(null);
      onEdgeSelect?.(null);
      cy.elements().unselect();
    };

    return () => {
      delete (window as any).__opmcvClearNodeSelection;
    };
  }, [onNodeSelect, onEdgeSelect]);

  // 验证 selectedNode 是否仍然有效（未被删除），避免 React 渲染已移除的 Cytoscape 节点导致 DOM 协调错误
  const isSelectedNodeValid = React.useMemo(() => {
    if (!selectedNode || !cy) return false;
    // Cytoscape 的 removed() 方法返回节点是否已从图中移除
    try {
      return (
        !selectedNode.removed() &&
        cy.getElementById(selectedNode.id()).length > 0
      );
    } catch {
      return false;
    }
  }, [selectedNode, cy, state.currentDiagram]);
  useEffect(() => {
    boxSelectedNodesRef.current = boxSelectedNodes;
  }, [boxSelectedNodes]);
  useEffect(() => {
    boxSelectionBoundsRef.current = boxSelectionBounds;
  }, [boxSelectionBounds]);

  // 监听图表切换，清除选中状态（避免控制点和菜单残留）
  useEffect(() => {
    setSelectedNode(null);
    setIsLabelSelected(false);
    setBoxSelectedNodes([]);
    setBoxSelectionBounds(null);
    setInteractionMode(InteractionMode.None);
    onNodeSelect?.(null);
    // 触发 BoxSelection 组件清除内部状态
    setClearSelectionTrigger(t => t + 1);

    if (cy) {
      if (shouldSkipDiagramRenderTracking(state.diagramChangeSource)) {
        console.log(
          '[OPMEditorCV] 跳过图层切换 render 跟踪:',
          state.diagramChangeSource,
          state.currentDiagram?.label
        );
        return;
      }
      // 初始导入期间跳过：importPlainJson 的 dispatch(CHANGE_DIAGRAM) 会触发此 effect，
      // 但渲染完成已由 useEffect([initialData]) 中注册的 cy.one('render') 处理，
      // 若此处再次 onRenderStart + 注册 handler 会导致竞态（handler 注册晚于 cy 渲染 → 永远卡住）
      if (!isInitialLoading) {
        onRenderStart?.();
        const finishRender = () => requestAnimationFrame(() => onRenderEnd?.());
        cy.one('render', finishRender);
      }
    }
  }, [state.currentDiagram, state.diagramChangeSource]);

  // 将屏幕坐标转换为画布坐标
  const screenToCanvas = useCallback(
    (clientX: number, clientY: number): Position => {
      if (!cy) return { x: clientX, y: clientY };
      const container = cy.container();
      if (!container) return { x: clientX, y: clientY };
      const rect = container.getBoundingClientRect();
      const pan = cy.pan();
      const zoom = cy.zoom();
      return {
        x: (clientX - rect.left - pan.x) / zoom,
        y: (clientY - rect.top - pan.y) / zoom,
      };
    },
    []
  );

  // 注册边创建事件处理（修改为左键连线）
  const registerEdgeEventHandlers = (cy: Core) => {
    let eh = cy.edgehandles(ehDefaults);
    ehRef.current = eh;

    // 监听所有 edgehandles 事件用于调试
    if (opmcvDebugEventsEnabled) {
      cy.on('ehstart', (evt: Event, sourceNode: NodeSingular) => {
        console.log('[EH Event] ehstart - sourceNode:', sourceNode?.id());
      });
    }

    // 边创建完成事件 - 删除 edgehandles 自动创建的边
    cy.on(
      'ehcomplete',
      (
        evt: Event,
        sourceNode: NodeSingular,
        targetNode: NodeSingular,
        addedEdge: any
      ) => {
        // edgehandles 会自动创建一条边，我们需要删除它
        // 因为我们要通过 EdgeEditDialog 来创建正确类型/label 的边
        if (addedEdge) {
          addedEdge.remove();
        }
      }
    );

    // 连线停止事件
    cy.on('ehstop', (evt: Event, sourceNode: NodeSingular) => {
      isConnectingRef.current = false;
      const callback = () => {
        // 手动连线：统一使用 EdgeEditDialog 创建边（EdgeTypeSelectionModal 保留作备份）
        setEdgeDialogMode('create');
        setEdgeDirection(getEdgeCreateDirectionInfo());
        setEditingEdge(null);
        // 创建连线时不预设类型，等待用户选择
        setEditingEdgeType(null);
        setEditingEdgeLabel('');
        setEditingEdgeQualifier(LinkQualifier.None);
        setEditingEdgeNegation(LinkNegation.None);
        setShowEdgeEditDialog(true);
      };
      edgeCreateValidate(callback);
    });

    // 连线拖动进入节点（edgehandles 扩展的事件）
    cy.on(
      'ehhoverover',
      (evt: Event, sourceNode: NodeSingular, targetNode: NodeSingular) => {
        edgeCreateDragOverElement({ target: targetNode });
      }
    );

    // 连线拖动离开节点（edgehandles 扩展的事件）
    cy.on(
      'ehhoverout',
      (evt: Event, sourceNode: NodeSingular, targetNode: NodeSingular) => {
        edgeCreateDragOutOfElement({ target: targetNode });
      }
    );

    // 监听 tapdrag 和 tapdragover 事件
    if (opmcvDebugEventsEnabled) {
      cy.on('tapdrag', (evt: Event) => {
        if (isConnectingRef.current) {
          console.log(
            '[Cytoscape] tapdrag while connecting - pos:',
            evt.position
          );
        }
      });

      cy.on('tapdragover', 'node', (evt: Event) => {
        console.log('[Cytoscape] tapdragover node:', evt.target?.id());
      });

      cy.on('tapdragout', 'node', (evt: Event) => {
        console.log('[Cytoscape] tapdragout node:', evt.target?.id());
      });
    }
  };

  const registerMarkerLabelHandlers = (cy: Core) => {
    let syncScheduled = false;
    let pendingEdges: any = null;
    let positionEventCount = 0;
    let positionDiagAt = 0;
    let markerDiagAt = 0;
    const scheduleSync = (edges?: any) => {
      if (edges && edges.length) {
        pendingEdges = pendingEdges ? pendingEdges.union(edges) : edges;
      } else {
        pendingEdges = cy.edges();
      }

      if (syncScheduled) return;
      syncScheduled = true;
      const sync = () => {
        const syncStartAt = isOpmcvDragDiagEnabled() ? opmcvDragDiagNow() : 0;
        syncScheduled = false;
        const edgesToSync = pendingEdges || cy.edges();
        pendingEdges = null;
        syncMarkerLabelNodes(cy, edgesToSync);

        if (isOpmcvDragDiagEnabled()) {
          const now = opmcvDragDiagNow();
          const duration = now - syncStartAt;

          if (duration >= 8 || now - markerDiagAt >= 600) {
            markerDiagAt = now;
            opmcvDragDiagLog(
              'marker-sync',
              `edges=${edgesToSync.length || 0}`,
              `duration=${duration.toFixed(1)}ms`
            );
          }
        }
      };
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(sync);
      } else {
        setTimeout(sync, 0);
      }
    };

    // 初始化已有连线的小标节点
    syncMarkerLabelNodes(cy, cy.edges());
    // 初次渲染完成后再同步一次，避免首次渲染前端点坐标不准确
    cy.one('render', () => scheduleSync());
    // 布局完成后同步位置
    cy.on('layoutstop', () => scheduleSync());

    // 连线增删与数据变化时同步小标节点
    cy.on('add', 'edge', (evt: Event) => {
      const edge = evt.target as EdgeSingular;
      scheduleSync(edge);
    });
    cy.on('data', 'edge', (evt: Event) => {
      const edge = evt.target as EdgeSingular;
      scheduleSync(edge);
    });
    cy.on('remove', 'edge', (evt: Event) => {
      const edge = evt.target as EdgeSingular;
      removeMarkerLabelNode(cy, edge);
    });

    // 节点移动时更新相关连线的小标位置
    cy.on('position', 'node', (evt: Event) => {
      const node = evt.target as NodeSingular;
      const edges = node.connectedEdges();
      if (!edges || edges.length === 0) return;

      if (isOpmcvDragDiagEnabled()) {
        positionEventCount += 1;
        const now = opmcvDragDiagNow();

        if (!positionDiagAt) {
          positionDiagAt = now;
        } else if (now - positionDiagAt >= 600) {
          opmcvDragDiagLog(
            'position-burst',
            `events=${positionEventCount}`,
            `last-node=${node.id()}`,
            `edges=${edges.length || 0}`
          );
          positionDiagAt = now;
          positionEventCount = 0;
        }
      }

      scheduleSync(edges);
    });
  };

  const registerPopperHandlers = (cy: Core) => {
    // 连线双击：弹出连线编辑弹框（不再使用 tippy 的 label 输入框）
    cy.on('dbltap', 'edge', (evt: any) => {
      const targetEdge = evt.target as EdgeSingular;
      if (!targetEdge) return;

      const mmRef = targetEdge.data('MMRef') as MMEdge | null;
      const currentType =
        (targetEdge.data('type') as EdgeType) ||
        mmRef?.type ||
        EdgeType.Consumption;

      let currentLabel = targetEdge.data('label') || '';
      if (mmRef) {
        // 派生边展示/导出的 label 以 originalEdges[0] 为准，这里也统一取该值作为默认输入
        if (mmRef.originalEdges?.length) {
          currentLabel = mmRef.originalEdges[0]?.label || currentLabel;
        } else {
          currentLabel = mmRef.label || currentLabel;
        }
      }

      setEdgeDialogMode('edit');
      setEdgeDirection(null);
      setEditingEdge(targetEdge);
      setEditingEdgeType(currentType);
      setEditingEdgeLabel(
        hierarchicalStructuralEdges.includes(currentType) ? '' : currentLabel
      );
      const currentQualifier =
        (targetEdge.data('linkQualifier') as LinkQualifier) ||
        mmRef?.linkQualifier ||
        LinkQualifier.None;
      const currentNegation =
        (targetEdge.data('linkNegation') as LinkNegation) ||
        mmRef?.linkNegation ||
        LinkNegation.None;
      const markerOption = getEdgeMarkerOption(currentType);
      let normalizedQualifier = currentQualifier;
      let normalizedNegation = currentNegation;
      if (markerOption) {
        if (!markerOption.qualifierOptions.includes(normalizedQualifier)) {
          normalizedQualifier =
            markerOption.defaultQualifier ?? LinkQualifier.None;
        }
        const allowedNegations = getAllowedNegations(
          markerOption,
          normalizedQualifier
        );
        if (!allowedNegations.includes(normalizedNegation)) {
          normalizedNegation =
            markerOption.defaultNegation ??
            allowedNegations[0] ??
            LinkNegation.None;
        }
      } else {
        normalizedQualifier = LinkQualifier.None;
        normalizedNegation = LinkNegation.None;
      }
      setEditingEdgeQualifier(normalizedQualifier);
      setEditingEdgeNegation(normalizedNegation);
      setShowEdgeEditDialog(true);
    });
  };

  const handleEdgeTypeChange = useCallback((nextType: EdgeType) => {
    setEditingEdgeType(nextType);
    if (hierarchicalStructuralEdges.includes(nextType)) {
      setEditingEdgeLabel('');
    }
    const option = getEdgeMarkerOption(nextType);
    if (option) {
      const nextQualifier = option.defaultQualifier ?? LinkQualifier.None;
      const allowedNegations = getAllowedNegations(option, nextQualifier);
      const nextNegation =
        option.defaultNegation ?? allowedNegations[0] ?? LinkNegation.None;
      setEditingEdgeQualifier(nextQualifier);
      setEditingEdgeNegation(nextNegation);
    } else {
      setEditingEdgeQualifier(LinkQualifier.None);
      setEditingEdgeNegation(LinkNegation.None);
    }
  }, []);

  const handleEdgeEditCancel = useCallback(() => {
    // 创建边时取消需要清理 edgehandles 的端点状态
    if (edgeDialogMode === 'create') {
      edgeCreateCancel();
    }
    setShowEdgeEditDialog(false);
    setEditingEdge(null);
    setEdgeDirection(null);
    setEditingEdgeLabel('');
    setEditingEdgeQualifier(LinkQualifier.None);
    setEditingEdgeNegation(LinkNegation.None);
  }, [edgeDialogMode]);

  const handleEdgeEditConfirm = useCallback(() => {
    if (edgeDialogMode === 'create') {
      if (!editingEdgeType) {
        // 创建连线时未选择类型，不执行创建
        return;
      }
      const isStructural =
        hierarchicalStructuralEdges.includes(editingEdgeType);
      const nextLabel = isStructural ? '' : (editingEdgeLabel || '').trim();
      // 创建边：由 edge.tsx 内部使用 edgehandles 端点创建实际边
      edgeCreate(
        editingEdgeType,
        state,
        nextLabel,
        editingEdgeQualifier,
        editingEdgeNegation
      );
      setShowEdgeEditDialog(false);
      setEditingEdge(null);
      setEdgeDirection(null);
      setEditingEdgeLabel('');
      setEditingEdgeQualifier(LinkQualifier.None);
      setEditingEdgeNegation(LinkNegation.None);
      return;
    }

    if (!cy || !editingEdge) {
      handleEdgeEditCancel();
      return;
    }

    const targetEdge = cy.getElementById(editingEdge.id());
    if (!targetEdge || targetEdge.empty()) {
      handleEdgeEditCancel();
      return;
    }

    const mmRef = targetEdge.data('MMRef') as MMEdge | null;
    const isStructural = hierarchicalStructuralEdges.includes(editingEdgeType);
    const nextLabel = isStructural ? '' : (editingEdgeLabel || '').trim();

    if (mmRef) {
      const allowedTypes = getAllowedEdgeTypesByMMNodes(
        mmRef.source,
        mmRef.target
      );
      if (!allowedTypes.includes(editingEdgeType)) {
        message.warning(
          getInvalidEdgeMessageByMMNodes(mmRef.source, mmRef.target) ||
            '当前节点组合不支持该连线类型'
        );
        return;
      }
    }

    // 没有 MMRef 的边（如剪贴板粘贴生成），只更新当前图上的 data
    if (!mmRef) {
      targetEdge.data({
        type: editingEdgeType,
        label: nextLabel,
        linkQualifier: editingEdgeQualifier,
        linkNegation: editingEdgeNegation,
        markerLabel: buildMarkerLabel(
          editingEdgeQualifier,
          editingEdgeNegation
        ),
        markerColor: getMarkerColorByEdge(null),
      });
      refreshStructuralEdgeStyles(cy, targetEdge);
      setShowEdgeEditDialog(false);
      setEditingEdge(null);
      return;
    }

    // 编辑边时同步 master model：派生边以 originalEdges 为“源”，需要反向写回 originalEdges
    const ownerEdges = mmRef.originalEdges?.length
      ? mmRef.originalEdges
      : [mmRef];
    const affected = new Set<MMEdge>();

    const ensureEdgeSnapshot = (edgeRef: MMEdge) => {
      if (edgeRef.__opmcvEditSnapshot) return;
      edgeRef.__opmcvEditSnapshot = {
        label: edgeRef.label || '',
        tags: Array.isArray(edgeRef.tags) ? [...edgeRef.tags] : [],
        description: edgeRef.description || '',
        instanceFileName: edgeRef.instanceFileName || '',
        instanceFileUrl: edgeRef.instanceFileUrl || '',
      };
    };

    const applyToOne = (edgeRef: MMEdge) => {
      ensureEdgeSnapshot(edgeRef);
      edgeRef.__opmcvDirty = true;
      edgeRef.type = editingEdgeType;
      edgeRef.label = nextLabel;
      edgeRef.linkQualifier = editingEdgeQualifier;
      edgeRef.linkNegation = editingEdgeNegation;
      affected.add(edgeRef);
      if (edgeRef.derivedEdges?.length) {
        edgeRef.derivedEdges.forEach(d => {
          d.type = editingEdgeType;
          d.label = nextLabel;
          d.linkQualifier = editingEdgeQualifier;
          d.linkNegation = editingEdgeNegation;
          affected.add(d);
        });
      }
      if (isStructural) {
        edgeRef.target.isStructurePart = true;
      }
    };

    ownerEdges.forEach(applyToOne);

    // 当前被点击的可能是派生边本身，也同步一份避免导出/切图时出现不一致
    if (!affected.has(mmRef)) {
      applyToOne(mmRef);
    }

    // 更新当前画布上所有相关边的 data，让样式/文字立即生效
    const updatedCyEdges: any[] = [];
    cy.edges().forEach((e: any) => {
      const ref = e.data('MMRef') as MMEdge | null;
      if (!ref) return;

      const shouldUpdate =
        affected.has(ref) ||
        ref.originalEdges?.some(orig => affected.has(orig));
      if (!shouldUpdate) return;

      const displayLabel = ref.originalEdges?.length
        ? ref.originalEdges[0]?.label || ''
        : ref.label || '';
      e.data({
        type: ref.type,
        label: displayLabel,
        linkQualifier: ref.linkQualifier,
        linkNegation: ref.linkNegation,
        markerLabel: buildMarkerLabel(ref.linkQualifier, ref.linkNegation),
        markerColor: getMarkerColorByEdge(ref),
      });
      updatedCyEdges.push(e);
    });

    refreshStructuralEdgeStyles(cy, updatedCyEdges);

    setShowEdgeEditDialog(false);
    setEditingEdge(null);
    setEdgeDirection(null);
    setEditingEdgeQualifier(LinkQualifier.None);
    setEditingEdgeNegation(LinkNegation.None);
  }, [
    edgeDialogMode,
    editingEdge,
    editingEdgeType,
    editingEdgeLabel,
    editingEdgeQualifier,
    editingEdgeNegation,
    handleEdgeEditCancel,
    state,
  ]);

  const mapLegacyType = (node: NodeSingular) => {
    const type = node.data('type');
    // 旧版 OPMEditor 的“过程”节点类型为 polygon（历史数据可能存在 circle，这里统一输出 polygon 以匹配现行逻辑）
    if (type === NodeType.Process) return 'polygon';
    if (type === NodeType.Object) return 'rect';
    return type || 'rect';
  };

  const collectMMNodeMap = () => {
    const map = new Map<string, MMNode>();
    const walk = (item: MMNode) => {
      map.set(item.id, item);
      if (item.children?.length) {
        item.children.forEach(child => walk(child));
      }
    };
    masterModelRoot.children.forEach(child => walk(child));
    return map;
  };

  const handleOpenImportDataDialog = useCallback((node: NodeSingular) => {
    setImportingNode(node);
    importingNodeRef.current = node;
    setShowImportDataDialog(true);
  }, []);

  const handleCloseImportDataDialog = useCallback(() => {
    setShowImportDataDialog(false);
    setImportingNode(null);
    importingNodeRef.current = null;
  }, []);

  const handleImportDataConfirm = useCallback(
    async (file: File) => {
      if (!cy || !importingNodeRef.current) {
        throw new Error('未找到选中的节点');
      }

      const targetNode = importingNodeRef.current;
      let hideMsg: (() => void) | null = null;
      try {
        setIsImporting(true);
        hideMsg = message.loading('正在导入数据，请稍候...', 0);

        const imported = await OPMApi.importData(file, targetNode.id());

        // 使用当前画布坐标参与旧逻辑的防重叠计算
        const existingNodes = cy.nodes().map(node => ({
          id: node.id(),
          x: node.position('x'),
          y: node.position('y'),
          width: node.width(),
          height: node.height(),
          type: mapLegacyType(node),
        }));

        const positioned = processImportedData(
          imported,
          targetNode.id(),
          existingNodes as any
        );

        // 后端仍返回 LogicFlow 结构：先生成坐标，再转为 Plain 数据
        const graphData = {
          nodes: positioned.nodes,
          edges: positioned.edges,
        };

        const plainPayload = convertOPMEditorToPlain(graphData);
        const plainNodes = plainPayload?.nodes || [];
        const plainEdges = plainPayload?.originalEdges || [];

        const positionMap = new Map(
          positioned.nodes.map(item => [item.id, item])
        );

        const mmMap = collectMMNodeMap();
        const createdNodes = new Map<string, MMNode>();

        // 构建/补充 master model 节点树
        const pending = new Map(plainNodes.map(n => [n.id, n]));
        let loopGuard = 0;
        while (pending.size && loopGuard < 200) {
          loopGuard += 1;
          let progressed = false;

          for (const [id, plain] of pending) {
            const parentId = plain.parentId || '';
            if (
              parentId &&
              !mmMap.has(parentId) &&
              !createdNodes.has(parentId)
            ) {
              continue;
            }

            if (!mmMap.has(id)) {
              const mmNode = new MMNode(
                id,
                plain.type as NodeType,
                plain.label || plain.type
              );
              // 兼容从 OPMEditor 导入的描述字段
              mmNode.description =
                plain.description ?? plain._legacyProperties?.description ?? '';
              mmNode.namespace = plain.namespace || '';
              mmNode.packageName = plain.packageName || '';
              // 同步英文名称，避免对象/过程节点回显为空。
              mmNode.codeName =
                plain.codeName ?? plain._legacyProperties?.codeName ?? '';
              // 同步扩展字段（与描述同级）
              mmNode.tags = Array.isArray(plain.tags)
                ? plain.tags
                : plain._legacyProperties?.tags || [];
              mmNode.objectType =
                plain.objectType ??
                plain._legacyProperties?.objectType ??
                mmNode.objectType;
              mmNode.exampleValues =
                plain.exampleValues ??
                plain._legacyProperties?.exampleValues ??
                '';
              mmNode.version =
                plain.version ??
                plain._legacyProperties?.version ??
                mmNode.version;
              mmNode.updatedAt =
                plain.updatedAt ?? plain._legacyProperties?.updatedAt ?? '';
              mmNode.updatedBy = Array.isArray(plain.updatedBy)
                ? plain.updatedBy
                : plain._legacyProperties?.updatedBy || [];
              mmNode.relatedSystem =
                plain.relatedSystem ??
                plain._legacyProperties?.relatedSystem ??
                '';
              mmNode.essence = plain.essence;
              mmNode.affiliation = plain.affiliation;
              mmNode.isStructurePart = plain.isStructurePart;
              mmNode.isSubelementOfMain = plain.isSubelementOfMain;
              mmNode.deleted = plain.deleted;
              mmNode.childGroupType =
                plain.childGroupType || ChildGroupType.None;

              const parentMM =
                (parentId &&
                  (createdNodes.get(parentId) || mmMap.get(parentId))) ||
                masterModelRoot;
              parentMM.addChild(mmNode);

              createdNodes.set(id, mmNode);
              mmMap.set(id, mmNode);
            }

            pending.delete(id);
            progressed = true;
          }

          if (!progressed) {
            const [id, plain] = pending.entries().next().value;
            const mmNode = new MMNode(
              id,
              plain.type as NodeType,
              plain.label || plain.type
            );
            // 兼容从 OPMEditor 导入的描述字段
            mmNode.description =
              plain.description ?? plain._legacyProperties?.description ?? '';
            mmNode.namespace = plain.namespace || '';
            mmNode.packageName = plain.packageName || '';
            // 同步英文名称，避免对象/过程节点回显为空。
            mmNode.codeName =
              plain.codeName ?? plain._legacyProperties?.codeName ?? '';
            // 同步扩展字段（与描述同级）
            mmNode.tags = Array.isArray(plain.tags)
              ? plain.tags
              : plain._legacyProperties?.tags || [];
            mmNode.objectType =
              plain.objectType ??
              plain._legacyProperties?.objectType ??
              mmNode.objectType;
            mmNode.exampleValues =
              plain.exampleValues ??
              plain._legacyProperties?.exampleValues ??
              '';
            mmNode.version =
              plain.version ??
              plain._legacyProperties?.version ??
              mmNode.version;
            mmNode.updatedAt =
              plain.updatedAt ?? plain._legacyProperties?.updatedAt ?? '';
            mmNode.updatedBy = Array.isArray(plain.updatedBy)
              ? plain.updatedBy
              : plain._legacyProperties?.updatedBy || [];
            mmNode.relatedSystem =
              plain.relatedSystem ??
              plain._legacyProperties?.relatedSystem ??
              '';
            mmNode.essence = plain.essence;
            mmNode.affiliation = plain.affiliation;
            mmNode.isStructurePart = plain.isStructurePart;
            mmNode.isSubelementOfMain = plain.isSubelementOfMain;
            mmNode.deleted = plain.deleted;
            mmNode.childGroupType = plain.childGroupType || ChildGroupType.None;
            masterModelRoot.addChild(mmNode);
            createdNodes.set(id, mmNode);
            mmMap.set(id, mmNode);
            pending.delete(id);
          }
        }

        // 将新节点落到当前图，并同步父子分组信息（单次批量渲染，减少重绘）
        recalculateNamespacesForMasterModel();
        const createdEntries = Array.from(createdNodes.entries());
        const parentGroupUpdates = new Map<string, ChildGroupType>();
        const nodeElements: any[] = [];
        createdEntries.forEach(([id, mmNode]) => {
          if (cy.getElementById(id).length > 0) return;
          const plain = plainNodes.find(n => n.id === id);
          const parentId = plain?.parentId || '';
          const position = positionMap.get(id);
          const label = plain?.label || plain?.type || mmNode.label;

          // 记录需要更新父节点分组类型
          if (
            (mmNode.type === NodeType.State ||
              mmNode.type === NodeType.Value) &&
            parentId
          ) {
            const nextGroup =
              mmNode.type === NodeType.State
                ? ChildGroupType.State
                : ChildGroupType.Value;
            const current =
              parentGroupUpdates.get(parentId) || ChildGroupType.None;
            // Value 比 State 优先显示 []，这里简单覆盖为最新
            parentGroupUpdates.set(parentId, nextGroup || current);
          }

          nodeElements.push({
            group: 'nodes',
            data: {
              id,
              label,
              displayLabel: computeDisplayLabel(label, mmNode),
              type: plain?.type || mmNode.type,
              parent: parentId || undefined,
              MMRef: mmNode,
              namespace: mmNode.namespace || '',
              packageName: mmNode.packageName || '',
              essence: mmNode.essence,
              affiliation: mmNode.affiliation,
              childGroupType: mmNode.childGroupType,
              customWidth: position?.width ?? plain?.width ?? undefined,
              customHeight: position?.height ?? plain?.height ?? undefined,
            },
            position: position
              ? { x: position.x, y: position.y }
              : {
                  x: targetNode.position('x') + 200,
                  y: targetNode.position('y'),
                },
          });
        });

        const edgeElements: any[] = [];
        plainEdges.forEach(edge => {
          const source = mmMap.get(edge.sourceId);
          const target = mmMap.get(edge.targetId);
          if (!source || !target) return;
          if (cy.getElementById(edge.id).length > 0) return;
          if (originalEdgeArray.edges.some(e => e.id === edge.id)) return;

          const mmEdge = new MMEdge(
            edge.id,
            source,
            target,
            edge.type as EdgeType,
            edge.propagation,
            undefined,
            edge.label,
            edge.linkQualifier || LinkQualifier.None,
            edge.linkNegation || LinkNegation.None
          );
          mmEdge.tags = Array.isArray(edge.tags) ? edge.tags : [];
          mmEdge.description = edge.description || '';
          mmEdge.version = edge.version || mmEdge.version;
          mmEdge.updatedAt = edge.updatedAt || '';
          mmEdge.updatedBy = Array.isArray(edge.updatedBy)
            ? edge.updatedBy
            : [];
          mmEdge.instanceFileName = edge.instanceFileName || '';
          mmEdge.instanceFileUrl = edge.instanceFileUrl || '';
          originalEdgeArray.addEdge(mmEdge);
          edgeElements.push({
            group: 'edges',
            data: {
              id: edge.id,
              label: edge.label,
              type: edge.type,
              source: edge.sourceId,
              target: edge.targetId,
              MMRef: mmEdge,
              linkQualifier: mmEdge.linkQualifier,
              linkNegation: mmEdge.linkNegation,
              markerLabel: buildMarkerLabel(
                mmEdge.linkQualifier,
                mmEdge.linkNegation
              ),
              markerColor: mmEdge.originalEdges.length ? '#ccc' : '#586D8C',
            },
          });
        });

        cy.startBatch();
        cy.add(nodeElements);
        cy.add(edgeElements);

        // 更新新节点的标签宽度
        nodeElements.forEach(ele => {
          const n = cy.getElementById(ele.data.id);
          if (n && n.length) {
            applyAutoLabelWidth(n, ele.data.label);
          }
        });

        // 更新父节点的分组标签与显示文本
        parentGroupUpdates.forEach((groupType, parentId) => {
          const parentMM = mmMap.get(parentId);
          if (!parentMM) return;
          if (parentMM.childGroupType === ChildGroupType.None) {
            parentMM.childGroupType = groupType;
          }
          const cyParent = cy.getElementById(parentId);
          if (cyParent.length > 0) {
            cyParent.data('childGroupType', parentMM.childGroupType);
            const displayLabel = computeDisplayLabel(parentMM.label, parentMM);
            cyParent.data('displayLabel', displayLabel);
            cyParent.style('label', displayLabel);
            applyAutoLabelWidth(cyParent, displayLabel);
          }
        });

        // 应用边标签
        edgeElements.forEach(ele => {
          if (ele.data.label) {
            const e = cy.getElementById(ele.data.id);
            if (e && e.length) {
              e.data('label', ele.data.label);
            }
          }
        });
        cy.endBatch();

        // 防止后续新增元素与导入ID冲突
        const currentCounter =
          typeof (eleCounter as any)._value === 'number'
            ? (eleCounter as any)._value
            : -1;
        const maxIdFromImport = plainNodes
          .map(n => n.id)
          .concat(plainEdges.map(e => e.id))
          .reduce((max, id) => {
            const matched = String(id).match(/(\\d+)$/);
            const num = matched ? Number(matched[1]) : NaN;
            return Number.isFinite(num) ? Math.max(max, num) : max;
          }, currentCounter);
        if (maxIdFromImport > currentCounter) {
          eleCounter.value = String(maxIdFromImport);
        }

        handleCloseImportDataDialog();
        message.success(
          `成功导入 ${plainNodes.length} 个节点和 ${plainEdges.length} 条边`
        );
        hideMsg && hideMsg();
        hideMsg = null;
      } catch (error: any) {
        console.error('导入数据失败', error);
        throw new Error(error?.message || '导入失败，请重试');
      } finally {
        setIsImporting(false);
        // 若异常提前返回，确保加载提示被关闭
        try {
          (hideMsg as any)?.();
        } catch (e) {
          // ignore
        }
      }
    },
    [handleCloseImportDataDialog]
  );

  /**
   * 在父图中抓取“目标根节点 1 跳范围”快照：
   * - 节点：根节点 + 1 跳邻居（状态/值会补齐其父节点以保持结构完整）
   * - 连线：仅保留两端都在上述节点集合中的连线（含根节点连线与 1 跳节点互连）
   */
  const collectInzoomSnapshot = useCallback(
    (rootNode: NodeSingular): InzoomSnapshot | null => {
      if (!cy || !rootNode || rootNode.empty()) return null;

      const rootId = rootNode.id();
      const rootMMRef = rootNode.data('MMRef') as MMNode | null;
      const rootParentId =
        rootNode.data('parent') ||
        (rootMMRef?.parent instanceof MMNode ? rootMMRef.parent.id : '');
      const includedNodeIds = new Set<string>([rootId]);

      rootNode.connectedEdges().forEach((edge: any) => {
        if (edge.data('display') === 'none') return;
        const sourceId = edge.data('source');
        const targetId = edge.data('target');
        if (!sourceId || !targetId) return;
        const neighbourId = sourceId === rootId ? targetId : sourceId;
        const neighbourNode = cy.getElementById(neighbourId);
        if (!neighbourNode || neighbourNode.empty()) return;
        if (neighbourNode.data('display') === 'none') return;
        if (neighbourNode.data('isMarkerLabel')) return;
        if (rootParentId && neighbourId === rootParentId) return;
        if (
          rootParentId &&
          (neighbourNode.data('type') === NodeType.State ||
            neighbourNode.data('type') === NodeType.Value) &&
          neighbourNode.data('parent') === rootParentId
        )
          return;
        includedNodeIds.add(neighbourId);
      });

      // 状态/值节点需要父节点才能正确挂载为 compound child，这里补齐其父节点。
      Array.from(includedNodeIds).forEach(nodeId => {
        const node = cy.getElementById(nodeId);
        if (!node || node.empty()) return;
        const type = node.data('type');
        if (type !== 'state' && type !== 'value') return;
        const parentId = node.data('parent');
        if (!parentId) return;
        if (rootParentId && parentId === rootParentId) return;
        const parentNode = cy.getElementById(parentId);
        if (!parentNode || parentNode.empty()) return;
        if (parentNode.data('display') === 'none') return;
        includedNodeIds.add(parentId);
        parentNode
          .children('[type="state"], [type="value"]')
          .forEach((sibling: any) => {
            if (sibling.data('display') === 'none') return;
            if (sibling.data('isMarkerLabel')) return;
            includedNodeIds.add(sibling.id());
          });
      });

      const nodes: InzoomNodeSnapshot[] = Array.from(includedNodeIds)
        .map(nodeId => {
          const node = cy.getElementById(nodeId);
          if (!node || node.empty()) return null;
          return {
            id: nodeId,
            position: {
              x: node.position().x,
              y: node.position().y,
            },
            data: node.data(),
          } as InzoomNodeSnapshot;
        })
        .filter(Boolean) as InzoomNodeSnapshot[];

      const edges: InzoomEdgeSnapshot[] = [];
      cy.edges().forEach((edge: any) => {
        if (edge.data('display') === 'none') return;
        const sourceId = edge.data('source');
        const targetId = edge.data('target');
        if (!sourceId || !targetId) return;
        if (!includedNodeIds.has(sourceId) || !includedNodeIds.has(targetId)) {
          return;
        }
        edges.push({
          id: edge.id(),
          MMRef: (edge.data('MMRef') as MMEdge) || null,
        });
      });

      return {
        rootId,
        nodes,
        edges,
      };
    },
    []
  );

  /**
   * 基于 1 跳快照在新子图中恢复节点与连线。
   */
  const restoreInzoomSnapshot = useCallback(
    (snapshot: InzoomSnapshot | null) => {
      if (!cy || !snapshot || !snapshot.nodes.length) return false;

      const viewport = cy.extent();
      const centerPos = {
        x: (viewport.x1 + viewport.x2) / 2,
        y: (viewport.y1 + viewport.y2) / 2,
      };

      const rootSnapshot =
        snapshot.nodes.find(node => node.id === snapshot.rootId) ||
        snapshot.nodes[0];
      const rootSourcePos = rootSnapshot.position;
      const nodeIdSet = new Set(snapshot.nodes.map(node => node.id));
      const parentMMNode = resolveDiagramTopLevelParent(
        currentDiagram.current.mainNode
      ) as MMNode | MMRoot;
      const cyRoot = cy.getElementById(snapshot.rootId);
      const rootTargetPos =
        cyRoot && cyRoot.length > 0 ? cyRoot.position() : centerPos;

      const sortedNodes = [...snapshot.nodes].sort((a, b) => {
        if (a.id === snapshot.rootId) return -1;
        if (b.id === snapshot.rootId) return 1;
        const aParent = a.data?.parent || '';
        const bParent = b.data?.parent || '';
        if (aParent && aParent === b.id) return 1;
        if (bParent && bParent === a.id) return -1;
        return 0;
      });
      const structureParentIds = new Set<string>();
      const structureChildBindings: Array<{
        childId: string;
        parentId: string;
      }> = [];

      sortedNodes.forEach(nodeSnapshot => {
        const nodeData = nodeSnapshot.data || {};
        const mmRef = nodeData.MMRef as MMNode | null;
        if (!mmRef) return;

        const originalParentId = nodeData.parent || '';
        const parentId =
          nodeSnapshot.id !== snapshot.rootId &&
          originalParentId &&
          nodeIdSet.has(originalParentId)
            ? originalParentId
            : '';
        const nodeType = mmRef.type || nodeData.type;
        if ((nodeType === 'state' || nodeType === 'value') && parentId) {
          structureParentIds.add(parentId);
          structureChildBindings.push({
            childId: nodeSnapshot.id,
            parentId,
          });
        }

        const position =
          nodeSnapshot.id === snapshot.rootId
            ? rootTargetPos
            : {
                x:
                  rootTargetPos.x + (nodeSnapshot.position.x - rootSourcePos.x),
                y:
                  rootTargetPos.y + (nodeSnapshot.position.y - rootSourcePos.y),
              };

        const customWidth = nodeData.customWidth ?? undefined;
        const customHeight = nodeData.customHeight ?? undefined;
        const hasCustomSize =
          customWidth !== undefined || customHeight !== undefined;
        const existedNode = cy.getElementById(nodeSnapshot.id);
        if (existedNode.length > 0) {
          existedNode.data({
            childGroupType: mmRef.childGroupType || ChildGroupType.None,
            customWidth,
            customHeight,
          });
          if (mmRef.codeName) {
            existedNode.data('codeName', mmRef.codeName);
          }
          if (hasCustomSize) {
            existedNode.style({
              ...(customWidth !== undefined
                ? { 'min-width': customWidth }
                : {}),
              ...(customHeight !== undefined
                ? { 'min-height': customHeight }
                : {}),
            });
          }
          return;
        }

        addNode(
          cy,
          {
            id: nodeSnapshot.id,
            label: mmRef.label || nodeData.label || nodeSnapshot.id,
            type: mmRef.type || nodeData.type,
            parent: parentId,
            MMRef: mmRef,
            customWidth,
            customHeight,
          },
          position,
          parentMMNode
        );

        const cyNode = cy.getElementById(nodeSnapshot.id);
        if (cyNode.length > 0) {
          cyNode.data({
            childGroupType: mmRef.childGroupType || ChildGroupType.None,
            customWidth,
            customHeight,
          });
          if (mmRef.codeName) {
            cyNode.data('codeName', mmRef.codeName);
          }
          if (hasCustomSize) {
            cyNode.style({
              ...(customWidth !== undefined
                ? { 'min-width': customWidth }
                : {}),
              ...(customHeight !== undefined
                ? { 'min-height': customHeight }
                : {}),
            });
          }
        }
      });

      structureChildBindings.forEach(({ childId, parentId }) => {
        const childNode = cy.getElementById(childId);
        const parentNode = cy.getElementById(parentId);
        if (!childNode || childNode.empty()) return;
        if (!parentNode || parentNode.empty()) return;
        childNode.move({ parent: parentId });
        childNode.data('parent', parentId);
        childNode.data('display', 'element');
      });

      structureParentIds.forEach(parentId => {
        const parentNode = cy.getElementById(parentId);
        if (!parentNode || parentNode.empty()) return;
        if (
          parentNode.children('[type="state"], [type="value"]').length === 0
        ) {
          return;
        }
        relayoutStateValueChildren(parentNode);
      });

      snapshot.edges.forEach(edgeSnapshot => {
        const edgeRef = edgeSnapshot.MMRef;
        if (!edgeRef || edgeRef.deleted) return;
        if (cy.getElementById(edgeRef.source.id).empty()) return;
        if (cy.getElementById(edgeRef.target.id).empty()) return;

        // 子图初始化时创建“当前图层本地边”，避免复用父图 MMEdge 导致后续编辑互相影响。
        const duplicatedEdgeExists = cy.edges().some((edge: any) => {
          const sourceId = edge.data('source');
          const targetId = edge.data('target');
          const type = edge.data('type');
          return (
            sourceId === edgeRef.source.id &&
            targetId === edgeRef.target.id &&
            type === edgeRef.type &&
            edge.data('display') !== 'none'
          );
        });
        if (duplicatedEdgeExists) return;

        const baseLabel = edgeRef.originalEdges.length
          ? edgeRef.originalEdges[0].label || ''
          : edgeRef.label || '';
        addEdge(
          cy,
          {
            id: eleCounter.value,
            label: baseLabel,
            type: edgeRef.type,
            MMRef: null,
            source: edgeRef.source,
            target: edgeRef.target,
            linkQualifier: edgeRef.linkQualifier || LinkQualifier.None,
            linkNegation: edgeRef.linkNegation || LinkNegation.None,
            markerColor: '#586D8C',
          },
          false
        );
      });

      if (cyRoot.length > 0) {
        const rootMMRef = cyRoot.data('MMRef') as MMNode | null;
        if (rootMMRef) {
          rootMMRef.childGroupType = ChildGroupType.InZoom;
        }
        cyRoot.data('childGroupType', ChildGroupType.InZoom);
        clampCompoundChildrenAwayFromTitle(cyRoot);
      }

      return true;
    },
    []
  );

  /**
   * 统计某节点在多少个图层中出现（按图层计数）。
   * 用于判断删除时是否应执行“仅当前图层删除”。
   */
  const countNodeDiagramAppearances = useCallback((nodeId: string): number => {
    if (!nodeId) return 0;
    let count = 0;

    const diagramHasNode = (diagram: DiagramTreeNode): boolean => {
      if (diagram === currentDiagram.current) {
        const currentNode = cy?.getElementById(nodeId);
        return !!(
          currentNode &&
          currentNode.length > 0 &&
          currentNode.data('display') !== 'none' &&
          !currentNode.data('isMarkerLabel') &&
          !currentNode.data('MMRef')?.deleted
        );
      }
      const nodes = diagram.diagramJson?.elements?.nodes || [];
      return nodes.some((item: any) => {
        const data = item?.data || {};
        if (data.isMarkerLabel) return false;
        if (data.display === 'none') return false;
        if (data.MMRef?.deleted) return false;
        const id = data.id || data.MMRefId || data.MMRef?.id;
        return String(id) === String(nodeId);
      });
    };

    const walk = (diagram: DiagramTreeNode) => {
      if (!diagram) return;
      if (diagramHasNode(diagram)) {
        count += 1;
      }
      (diagram.children || []).forEach(child => walk(child));
    };

    walk(diagramTreeRoot);
    return count;
  }, []);

  /**
   * state/value 子节点若挂在跨图层级联父节点下，删除时应执行跨图层同步删除。
   */
  const shouldCascadeDeleteStateValueNode = useCallback(
    (nodeMMRef: MMNode | null): boolean => {
      if (!nodeMMRef) return false;
      if (
        nodeMMRef.type !== NodeType.State &&
        nodeMMRef.type !== NodeType.Value
      ) {
        return false;
      }
      const parentMMRef = nodeMMRef.parent;
      if (!(parentMMRef instanceof MMNode)) return false;
      return countNodeDiagramAppearances(String(parentMMRef.id)) > 1;
    },
    [countNodeDiagramAppearances]
  );

  /**
   * 仅在当前图层移除节点，不影响 master model 与其他图层。
   */
  const removeNodeInCurrentDiagramOnly = useCallback(
    (node: NodeSingular) => {
      const nodesToRemove = node.union(node.descendants());
      const nodeIdSet = new Set(nodesToRemove.map((item: any) => item.id()));

      setSelectedNode(null);
      onNodeSelect?.(null);
      setIsLabelSelected(false);

      nodesToRemove.remove();

      return nodeIdSet;
    },
    [onNodeSelect]
  );

  const isCurrentSubdiagramRootNode = useCallback((node: NodeSingular) => {
    const activeDiagram = currentDiagram.current;
    if (!(activeDiagram?.mainNode instanceof MMNode)) return false;
    const nodeMMRef = node?.data('MMRef') as MMNode | null;
    return !!nodeMMRef && activeDiagram.mainNode === nodeMMRef;
  }, []);

  const confirmDeleteNodeWithChildren = useCallback(
    () =>
      new Promise<boolean>(resolve => {
        Modal.confirm({
          title: '确认删除吗？',
          content: '删除该节点时将同步删除其包含的所有下级子节点。',
          centered: true,
          okText: '删除',
          okType: 'danger',
          cancelText: '取消',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
    []
  );

  const confirmDeleteSubdiagramRootNode = useCallback(
    () =>
      new Promise<boolean>(resolve => {
        Modal.confirm({
          title: '您确定删除该节点吗？',
          content: '删除该节点会同步删除子图，删除后则无法恢复',
          centered: true,
          okText: '删除',
          okType: 'danger',
          cancelText: '取消',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
    []
  );

  const hasActiveChildNodes = useCallback((node: NodeSingular) => {
    const nodeMMRef = node?.data('MMRef') as MMNode | null;
    if ((nodeMMRef?.children || []).some(child => !child?.deleted)) {
      return true;
    }
    return node.children().length > 0 || node.descendants().length > 0;
  }, []);

  const shouldTreatAsDiagramRootNode = useCallback(
    (nodeMMRef: MMNode | null) => {
      if (!nodeMMRef) return false;
      const activeDiagram = currentDiagram.current;
      if (
        activeDiagram?.mainNode instanceof MMNode &&
        activeDiagram.mainNode === nodeMMRef
      ) {
        return true;
      }
      return !!nodeMMRef.diagram && nodeMMRef.diagram.parent === activeDiagram;
    },
    []
  );

  const deleteCurrentSubdiagramByRootNode = useCallback(
    (node: NodeSingular) => {
      if (!cy || !isCurrentSubdiagramRootNode(node)) return false;
      const activeDiagram = currentDiagram.current;
      const parentDiagram = activeDiagram?.parent;
      const nodeMMRef = node.data('MMRef') as MMNode | null;
      if (!activeDiagram || !parentDiagram || !nodeMMRef) return false;

      switchDiagrams(activeDiagram, parentDiagram);
      detachDiagramSubtree(activeDiagram);
      dispatch({ type: ACTIONS.CHANGE_DIAGRAM, payload: parentDiagram });
      dispatch({ type: ACTIONS.UPDATE_TREE });
      setSelectedNode(null);
      onNodeSelect?.(null);
      setIsLabelSelected(false);
      return true;
    },
    [dispatch, isCurrentSubdiagramRootNode, onNodeSelect]
  );

  const resetViewWhenViewportHasNoNodes = useCallback(() => {
    const sync = () => {
      if (!cy) return;
      const viewport = cy.extent();
      const hasVisibleNodeInViewport = cy.nodes().some(node => {
        if (node.data('display') === 'none' || node.data('isMarkerLabel')) {
          return false;
        }
        const pos = node.position();
        if (!Number.isFinite(pos.x) || !Number.isFinite(pos.y)) {
          return false;
        }
        const bb = node.boundingBox({ includeLabels: false });
        return (
          bb.x2 >= viewport.x1 &&
          bb.x1 <= viewport.x2 &&
          bb.y2 >= viewport.y1 &&
          bb.y1 <= viewport.y2
        );
      });

      // 进入子图后如果当前视野内看不到任何节点，则自动重置视图。
      if (!hasVisibleNodeInViewport) {
        smartFitView(cy, 80);
      }
    };

    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => window.requestAnimationFrame(sync));
    } else {
      setTimeout(sync, 0);
    }
  }, []);
  /**
   * 判断边是否仍被其他图层复用（兼容历史共享边数据）。
   */
  const isEdgeSharedWithOtherDiagrams = useCallback(
    (edgeId: string): boolean => {
      if (!edgeId) return false;
      let shared = false;
      const walk = (diagram: DiagramTreeNode) => {
        if (!diagram || shared) return;
        if (diagram !== currentDiagram.current) {
          const edges = diagram.diagramJson?.elements?.edges || [];
          const hit = edges.some((item: any) => {
            const data = item?.data || {};
            const id = data.id || data.MMRef?.id;
            return String(id) === String(edgeId);
          });
          if (hit) {
            shared = true;
            return;
          }
        }
        (diagram.children || []).forEach(child => walk(child));
      };
      walk(diagramTreeRoot);
      return shared;
    },
    []
  );

  // 节点操作菜单项处理函数
  const menuHandlers = {
    onHide: (node: NodeSingular) => {
      node.data({ lastParent: node.data('parent') });
      node.move({ parent: null });
      node.data({ display: 'none' });
      for (const child of node.children()) {
        child.data({ display: 'none' });
      }
      setSelectedNode(null);
      onNodeSelect?.(null);
      setIsLabelSelected(false);
    },
    onRemove: (node: NodeSingular) => {
      if (isCurrentSubdiagramRootNode(node)) {
        void confirmDeleteSubdiagramRootNode().then(confirmed => {
          if (!confirmed) return;
          deleteCurrentSubdiagramByRootNode(node);
        });
        return;
      }
      const proceedRemove = () => {
        const nodeId = node.id();
        const appearanceCount = countNodeDiagramAppearances(nodeId);
        const nodeMMRef = node.data('MMRef') as MMNode | null;
        const shouldCascadeDelete =
          shouldCascadeDeleteStateValueNode(nodeMMRef);
        const shouldTreatAsDiagramRoot =
          shouldTreatAsDiagramRootNode(nodeMMRef);

        if (
          appearanceCount > 1 &&
          nodeMMRef &&
          !shouldCascadeDelete &&
          !shouldTreatAsDiagramRoot
        ) {
          removeNodeInCurrentDiagramOnly(node);
          return;
        }

        removeNodeContextMenu(node, dispatch, currentDiagram.current);
        node.remove();
        setSelectedNode(null);
        onNodeSelect?.(null);
        setIsLabelSelected(false);
      };

      if (hasActiveChildNodes(node)) {
        void confirmDeleteNodeWithChildren().then(confirmed => {
          if (!confirmed) return;
          proceedRemove();
        });
        return;
      }

      proceedRemove();
    },
    onDuplicate: (node: NodeSingular) => {
      // 选中目标节点
      cy.elements().unselect();
      node.select();
      // 复制并粘贴
      handleCopy();
      setTimeout(() => {
        handlePaste();
      }, 50);
    },
    onInZoom: (node: NodeSingular) => {
      // 图层切换前先清除搜索状态，避免搜索过滤影响新图层显示（搜索仅在当前图层生效）
      if (typeof (window as any).__clearSearchAndRestore === 'function') {
        (window as any).__clearSearchAndRestore();
      }
      let nextDiagram;
      const MMReference = node.data('MMRef');
      if (!MMReference) {
        console.warn(
          '[OPMCV] onInZoom skipped: missing MMRef for node:',
          node.id()
        );
        return;
      }
      // 当前图的主节点不允许继续展开子图（与旧版 OPMEditor 行为保持一致）
      if (
        currentDiagram.current?.mainNode &&
        MMReference === currentDiagram.current.mainNode
      ) {
        message.warning('当前图的主节点不支持展开子图');
        return;
      }
      // 注意：主节点的 diagram 指向“当前图”本身（node.diagram === currentDiagram）时，
      // 继续展开应该创建一个“子图”，但不能覆盖 MMReference.diagram（否则会导致从上层无法再打开原本的子图）。
      if (
        (nextDiagram = MMReference.diagram) &&
        nextDiagram !== currentDiagram.current
      ) {
        runWithDiagramSwitching(() => {
          // 注意：cy.json() 会包含 style，且样式表里存在函数值（如 taxi-turn），序列化/反序列化会导致 value 为 null 进而触发 Cytoscape 报错
          const json = cy.json();
          delete json.style;
          currentDiagram.current.diagramJson = json;
          cy.elements().remove();
          const nextJson = nextDiagram.diagramJson
            ? { ...nextDiagram.diagramJson }
            : null;
          if (nextJson) {
            delete nextJson.style;
          }
          cy.json(nextJson);
          // 重新应用样式表，确保选中状态等样式正常工作
          cy.style().fromJson(cyStylesheet).update();
          syncSubdiagramVisualStateFromMM(cy);
        });
        dispatch({ type: ACTIONS.CHANGE_DIAGRAM, payload: nextDiagram });
        if (!resetSubdiagramViewOnInitialOpen(nextDiagram)) {
          resetViewWhenViewportHasNoNodes();
        }
        return;
      }
      // 首次创建子图前，先在父图抓取 1 跳节点与连线快照。
      const inzoomSnapshot = collectInzoomSnapshot(node);

      // 同上：保存 diagramJson 时剔除 style，避免后续切换回来时触发 Cytoscape 解析异常
      const json = cy.json();
      delete json.style;
      currentDiagram.current.diagramJson = json;
      cy.elements().remove();
      nextDiagram = new DiagramTreeNode('', MMReference);
      // 仅当该节点还没有 diagram 时才写回（避免覆盖已存在的子图引用）
      if (!MMReference.diagram) {
        MMReference.diagram = nextDiagram;
      }
      currentDiagram.current.addChild(nextDiagram);

      // 保持原有行为：首次展开子图时，先创建默认子元素。
      const fakeEvent = { target: node, position: node.position() };
      addInzoomedNodes(cy, fakeEvent);

      // 新增能力：在原有行为上补充“根节点 1 跳范围”节点与连线同步。
      restoreInzoomSnapshot(inzoomSnapshot);
      syncSubdiagramVisualStateFromMM(cy);

      dispatch({ type: ACTIONS.INZOOM_DIAGRAM, payload: nextDiagram });
      setSelectedNode(null);
      onNodeSelect?.(null);
      setIsLabelSelected(false);
      if (!resetSubdiagramViewOnInitialOpen(nextDiagram)) {
        resetViewWhenViewportHasNoNodes();
      }
    },
    onAddState: (node: NodeSingular) => {
      const nodeMMRef = node.data('MMRef') as MMNode;
      // 检查是否是首次添加状态
      const existingStateCount = (nodeMMRef?.children || []).filter(
        child => child.type === NodeType.State && !child.deleted
      ).length;
      if (existingStateCount === 0) {
        const invalidStateAdditionMessage = getInvalidStateAdditionMessage(
          nodeMMRef,
          originalEdgeArray.edges
        );
        if (invalidStateAdditionMessage) {
          message.warning(invalidStateAdditionMessage);
          return;
        }
      }

      const fakeEvent = { target: node, position: node.position() };
      if (existingStateCount === 0) {
        // 首次添加状态：创建两个状态
        addNodeFromContextMenu(cy, fakeEvent, 'state', currentDiagram.current);
        addNodeFromContextMenu(cy, fakeEvent, 'state', currentDiagram.current);
      } else {
        // 后续添加状态：创建一个
        addNodeFromContextMenu(cy, fakeEvent, 'state', currentDiagram.current);
      }
      // 强制刷新菜单：重新获取节点并设置，触发菜单重新计算
      const updatedNode = cy.getElementById(node.id());
      setSelectedNode(null);
      setTimeout(() => setSelectedNode(updatedNode), 0);
    },
    onAddValue: (node: NodeSingular) => {
      const fakeEvent = { target: node, position: node.position() };
      // 检查是否是首次添加值
      const existingValues = node.children('[type="value"]');
      if (existingValues.length === 0) {
        // 首次添加值：创建两个值节点
        addNodeFromContextMenu(cy, fakeEvent, 'value', currentDiagram.current);
        addNodeFromContextMenu(cy, fakeEvent, 'value', currentDiagram.current);
      } else {
        // 后续添加值：创建一个
        addNodeFromContextMenu(cy, fakeEvent, 'value', currentDiagram.current);
      }
      // 强制刷新菜单：重新获取节点并设置，触发菜单重新计算
      const updatedNode = cy.getElementById(node.id());
      setSelectedNode(null);
      setTimeout(() => setSelectedNode(updatedNode), 0);
    },
    onAddObject: (node: NodeSingular) => {
      const fakeEvent = { target: node, position: node.position() };
      addNodeFromContextMenu(cy, fakeEvent, 'object', currentDiagram.current);
    },
    onAddProcess: (node: NodeSingular) => {
      const fakeEvent = { target: node, position: node.position() };
      addNodeFromContextMenu(cy, fakeEvent, 'process', currentDiagram.current);
    },
    onDataMap: (node: NodeSingular) => {
      if (isDataMapNavigating) {
        return;
      }
      if (!onDataMap) {
        message.warning('数据映射功能暂不可用');
        return;
      }
      void onDataMap(node);
    },
    onFunctionDefinition: (node: NodeSingular) => {
      setFunctionNode(node);
      setShowFunctionDialog(true);
      onNodeSelect?.(null);
    },
    onChangeEssence: (node: NodeSingular) => {
      const MMRef = node.data('MMRef');
      if (MMRef.essence === Essence.Informatical)
        MMRef.essence = Essence.Physical;
      else MMRef.essence = Essence.Informatical;
      node.data({ labelWidth: node.width() + 1 });
    },
    onChangeAffiliation: (node: NodeSingular) => {
      const MMRef = node.data('MMRef');
      if (MMRef.affiliation === Affiliation.Systemic)
        MMRef.affiliation = Affiliation.Environmental;
      else MMRef.affiliation = Affiliation.Systemic;
      node.data({ labelWidth: node.width() + 1 });
    },
    onBringConnected: (node: NodeSingular) => {
      dispatch({
        type: ACTIONS.EDGE_SELECTION,
        payload: { show: true, node: node.data('MMRef') },
      });
    },
    onBringAllStates: (node: NodeSingular) => {
      bringAllStates(cy, node);
    },
    onShowHidden: () => {
      for (const element of cy.elements()) {
        if (element.data('display') === 'none') {
          const parentId = element.data('lastParent');
          element.move({ parent: parentId });
          element.data({ display: 'element' });
        }
      }
    },
  };

  const hasObjectProcessExhibition =
    !!selectedNode &&
    selectedNode.data('type') === NodeType.Object &&
    selectedNode.connectedEdges().some(edge => {
      if (edge.data('type') !== EdgeType.Exhibition) {
        return false;
      }

      const source = edge.source();
      const target = edge.target();
      const sourceType = source?.data('type');
      const targetType = target?.data('type');

      return (
        (source.id() === selectedNode.id() &&
          targetType === NodeType.Process) ||
        (target.id() === selectedNode.id() && sourceType === NodeType.Process)
      );
    });

  const isProcessNodeSelected =
    !!selectedNode && selectedNode.data('type') === NodeType.Process;
  const isStateNodeSelected =
    !!selectedNode && selectedNode.data('type') === NodeType.State;

  // 父类对象继承的属性：沿 Generalization 边向上遍历祖先节点，
  // 若祖先有 Exhibition 出边属性或状态子节点，则认为当前节点有继承属性
  // 与属性面板「属性概览 - 父类对象继承的属性」的计算逻辑一致
  const hasInheritedAttributesFromParent =
    !!selectedNode &&
    selectedNode.data('type') === NodeType.Object &&
    (() => {
      const visited = new Set<string>([selectedNode.id()]);
      const stack: any[] = [selectedNode];
      while (stack.length) {
        const current = stack.pop();
        for (const edge of current.connectedEdges().toArray()) {
          if (edge.data('type') !== EdgeType.Generalization) continue;
          if (edge.target().id() !== current.id()) continue;
          const parent = edge.source();
          const parentId = parent?.id?.();
          if (!parentId || visited.has(parentId)) continue;
          visited.add(parentId);
          // 祖先有 Exhibition 出边属性
          const hasExhibition = parent
            .connectedEdges()
            .some(
              (pEdge: any) =>
                pEdge.data('type') === EdgeType.Exhibition &&
                pEdge.source().id() === parentId
            );
          if (hasExhibition) return true;
          // 祖先有状态子节点
          if (parent.children('[type="state"]').length > 0) return true;
          stack.push(parent);
        }
      }
      return false;
    })();

  const canOpenDataMap =
    !!selectedNode &&
    !isProcessNodeSelected &&
    !isStateNodeSelected &&
    !hasObjectProcessExhibition &&
    (selectedNode.connectedEdges().some(edge => {
      return (
        edge.data('type') === EdgeType.Exhibition &&
        edge.source().id() === selectedNode.id()
      );
    }) ||
      hasInheritedAttributesFromParent);

  // 创建菜单项
  const menuItems: MenuItem[] = createDefaultMenuItems(
    dispatch,
    currentDiagram.current,
    menuHandlers
  ).map(item =>
    item.id === 'data-map'
      ? {
          ...item,
          disabled: (node: NodeSingular) => {
            const originalDisabled =
              typeof item.disabled === 'function'
                ? item.disabled(node)
                : !!item.disabled;
            return originalDisabled || !!isDataMapNavigating || !canOpenDataMap;
          },
        }
      : item.id === 'function-definition'
        ? {
            ...item,
            visible: (node: NodeSingular) => {
              if (!showFunctionDefinitionMenu) {
                return false;
              }

              if (item.visible) {
                return item.visible(node);
              }

              return true;
            },
          }
        : item
  );

  // 注册新的交互事件
  const registerInteractionHandlers = (cy: Core) => {
    // 禁用默认右键菜单
    cy.container()?.addEventListener('contextmenu', e => {
      e.preventDefault();
    });

    // 鼠标移动 - 更新光标样式
    cy.on('mousemove', evt => {
      if (isConnectingRef.current) return;

      const container = cy.container();
      if (!container) return;

      const pos = evt.position;
      // 检查鼠标是否在节点上
      const nodesAtPoint = cy.nodes().filter((node: NodeSingular) => {
        const bb = node.boundingBox({ includeLabels: false });
        return (
          pos.x >= bb.x1 && pos.x <= bb.x2 && pos.y >= bb.y1 && pos.y <= bb.y2
        );
      });

      if (nodesAtPoint.length > 0) {
        const topNode = nodesAtPoint[nodesAtPoint.length - 1];
        // 检查是否在可拖动区域（文字区域或中心区域）
        if (isPointInDraggableArea(topNode, pos)) {
          container.style.cursor = 'move'; // 可移动
        } else {
          container.style.cursor = 'crosshair'; // 可连线
        }
      } else {
        container.style.cursor = 'default';
      }
    });

    // 左键按下节点 - 区分文字区域和非文字区域
    cy.on('tapstart', 'node', evt => {
      // 节点编辑必填校验未通过时：阻止切换选中节点，保持编辑焦点
      if (
        typeof window !== 'undefined' &&
        (window as any).__opmcvNodeEditorBlockClearSelection
      ) {
        return;
      }
      const node = evt.target as NodeSingular;
      const pos = evt.position;
      tapStartPosRef.current = pos;
      dragStartedRef.current = false;
      dragEndedRef.current = false;
      dragMovedRef.current = false;

      // 检查是否有框选节点，且当前点击的节点在框选列表中
      const currentBoxSelectedNodes = boxSelectedNodesRef.current;
      const isInBoxSelection = currentBoxSelectedNodes.some(
        n => n.id() === node.id()
      );

      // 如果在框选列表中，跳过单节点处理，让框选拖动逻辑生效
      if (isInBoxSelection) {
        return;
      }

      // 检查是否点击在可拖动区域（文字区域或中心区域）
      if (isPointInDraggableArea(node, pos)) {
        // 点击可拖动区域 - 选中节点，显示控制点和菜单
        setSelectedNode(node);
        setIsLabelSelected(true);
        setInteractionMode(InteractionMode.Selected);
        // 允许拖动
        node.grabify();
      } else {
        // 点击非可拖动区域 - 进入连线模式
        if (ehRef.current && !isConnectingRef.current) {
          isConnectingRef.current = true;
          edgeCreateStart(ehRef.current, evt);
          setInteractionMode(InteractionMode.Connecting);
        }
      }
    });

    // 注意：连线拖动进入/离开节点由 edgehandles 扩展的 ehover/ehout 事件处理
    // 不需要额外添加 tapdragover/tapdragout

    // 点击画布空白处 - 取消选择
    cy.on('tap', evt => {
      if (evt.target === cy) {
        // 点击空白处
        const clickPos = evt.position;
        const bounds = boxSelectionBoundsRef.current;

        // 检查是否点击在框选区域内
        const isInBoxSelection =
          bounds &&
          clickPos.x >= bounds.x1 &&
          clickPos.x <= bounds.x2 &&
          clickPos.y >= bounds.y1 &&
          clickPos.y <= bounds.y2;

        // 如果点击在框选区域内，不清除框选，让 BoxSelection 处理拖动
        if (isInBoxSelection) {
          return;
        }

        if (isConnectingRef.current) {
          // 取消连线
          edgeCreateCancel();
          isConnectingRef.current = false;
        }

        // 节点编辑必填校验未通过时：阻止点击空白处清空选中，保持编辑焦点
        if (
          typeof window !== 'undefined' &&
          (window as any).__opmcvNodeEditorBlockClearSelection
        ) {
          return;
        }
        setSelectedNode(null);
        onNodeSelect?.(null);
        onEdgeSelect?.(null);
        setIsLabelSelected(false);
        setInteractionMode(InteractionMode.None);
        // 清除所有选中状态（包括 cytoscape 原生选中和 box-selected 类）
        cy.elements().unselect();
        cy.$('.box-selected').removeClass('box-selected');
        setBoxSelectedNodes([]);
        setBoxSelectionBounds(null);
      }
    });

    // 节点拖动 - 只有文字区域被选中时才能拖动
    cy.on('grab', 'node', evt => {
      const node = evt.target as NodeSingular;
      dragStartedRef.current = true;
      dragMovedRef.current = false;
      // 使用 ref 获取最新状态
      const currentIsLabelSelected = isLabelSelectedRef.current;
      const currentSelectedNode = selectedNodeRef.current;
      const currentBoxSelectedNodes = boxSelectedNodesRef.current;
      let allowDrag =
        currentIsLabelSelected && currentSelectedNode?.id() === node.id();

      // 如果不是通过文字区域选中的，阻止拖动
      if (!allowDrag) {
        // 检查是否是框选的节点
        const isBoxSelected = currentBoxSelectedNodes.some(
          n => n.id() === node.id()
        );
        allowDrag = isBoxSelected;
        if (!allowDrag) {
          activeDraggedNodeIdRef.current = null;
          node.ungrabify();
        }
      }

      if (allowDrag) {
        activeDraggedNodeIdRef.current = node.id();
      }
    });

    cy.on('drag', 'node', () => {
      dragMovedRef.current = true;
    });

    cy.on('dragfree', 'node', evt => {
      dragEndedRef.current = true;
      dragStartedRef.current = false;
      const draggedNode = evt.target as NodeSingular;
      const isDirectlyDraggedNode =
        activeDraggedNodeIdRef.current === draggedNode.id();

      if (!isDirectlyDraggedNode) {
        return;
      }
      syncNodeParentAfterDrag(draggedNode);
      clampChildNodeWithinParentTitleSafeArea(draggedNode);
      activeDraggedNodeIdRef.current = null;
      const nodeId = evt.target?.id?.();
      if (
        typeof window !== 'undefined' &&
        (window as any).__closeEditorIfNotNode
      ) {
        (window as any).__closeEditorIfNotNode(nodeId);
      }
    });

    cy.on('tapend', 'node', evt => {
      const node = evt.target as NodeSingular;
      const pending = selectedNodeRef.current;
      // 仅在未产生拖动且当前节点仍为选中节点时触发外部选择
      if (
        !dragStartedRef.current &&
        !dragEndedRef.current &&
        !dragMovedRef.current &&
        pending &&
        pending.id() === node.id()
      ) {
        onNodeSelect?.(node);
        onEdgeSelect?.(null);
      }
      tapStartPosRef.current = null;
      activeDraggedNodeIdRef.current = null;
      dragStartedRef.current = false;
      dragEndedRef.current = false;
      dragMovedRef.current = false;
    });

    cy.on('tap', 'edge', evt => {
      if (
        typeof window !== 'undefined' &&
        (window as any).__opmcvNodeEditorBlockClearSelection
      ) {
        return;
      }
      const edge = evt.target as EdgeSingular;
      if (!edge) return;
      setSelectedNode(null);
      setIsLabelSelected(false);
      setInteractionMode(InteractionMode.None);
      onNodeSelect?.(null);
      onEdgeSelect?.(edge);
      cy.elements().unselect();
      edge.select();
    });

    // 节点位置变化时清除缓存并更新相关边的动态样式
    cy.on('position', 'node', evt => {
      const node = evt.target as NodeSingular;
      // 子节点拖动过程中实时避开父节点标题安全区。
      if (activeDraggedNodeIdRef.current === node.id()) {
        clampChildNodeWithinParentTitleSafeArea(node);
      }

      clearLabelBBoxCache(node.id());
      // 更新与该节点相关的边的样式（触发 taxi-turn 等动态样式重新计算）
      node.connectedEdges().updateStyle();
    });
  };

  const createNodeAtPosition = useCallback(
    (nodeType: 'object' | 'process', position: { x: number; y: number }) => {
      if (!cy || !currentDiagram.current) return;

      // 右侧拖拽创建节点时，按“全图层全局唯一”规则生成名称：Object1/Object2...、Process1/Process2...
      const baseLabel = nodeType === 'object' ? 'Object' : 'Process';
      const usedLabels = buildUsedNodeLabelSet();
      const usedCodeNames = buildUsedNodeCodeNameSet();
      const uniqueLabel = generateUniqueSequencedValue(
        baseLabel,
        usedLabels,
        baseLabel
      );
      const uniqueCodeName = generateUniqueCodeNameByLabel(
        uniqueLabel,
        usedCodeNames
      );

      // 检查位置是否在某个父节点（compound node）的范围内
      // 父节点是指已经有子节点的节点（如 in-zoom 后的节点或有状态的对象）
      let targetParentNode: NodeSingular | null = null;
      cy.nodes().forEach((node: NodeSingular) => {
        // 只检查父节点（有子节点的节点）
        if (!node.isParent()) return;
        // 跳过隐藏的节点
        if (node.data('display') === 'none') return;
        // 跳过状态节点
        if (node.data('type') === 'state') return;

        // 只允许 InZoom 父节点接受拖拽的对象/过程
        // 状态/值父节点不应该接受拖拽添加（只能通过菜单添加对应类型的子节点）
        const childGroupType = node.data('childGroupType') as ChildGroupType;
        if (childGroupType !== ChildGroupType.InZoom) return;

        const bb = node.boundingBox({ includeLabels: false });
        if (
          position.x >= bb.x1 &&
          position.x <= bb.x2 &&
          position.y >= bb.y1 &&
          position.y <= bb.y2
        ) {
          // 如果找到多个嵌套的父节点，选择最内层的（面积最小的）
          if (
            !targetParentNode ||
            bb.w * bb.h <
              targetParentNode.boundingBox({ includeLabels: false }).w *
                targetParentNode.boundingBox({ includeLabels: false }).h
          ) {
            targetParentNode = node;
          }
        }
      });

      const counter = eleCounter.value;

      if (targetParentNode) {
        // 在父节点范围内创建，作为其子节点
        const parentMMRef = targetParentNode.data('MMRef') as MMNode;
        const data = {
          id: counter,
          label: uniqueLabel,
          type: nodeType === 'object' ? NodeType.Object : NodeType.Process,
          parent: targetParentNode.id(),
          MMRef: null,
        };
        addNode(cy, data, position, parentMMRef);
      } else {
        // 在空白区域创建，作为普通节点
        const parentMMNode = resolveDiagramTopLevelParent(
          currentDiagram.current.mainNode
        ) as MMNode | MMRoot;
        const data = {
          id: counter,
          label: uniqueLabel,
          type: nodeType === 'object' ? NodeType.Object : NodeType.Process,
          parent: '',
          MMRef: null,
        };
        addNode(cy, data, position, parentMMNode);
      }

      // 新增节点时同步生成英文名称（codeName），并遵守全图层唯一规则。
      const createdNode = cy.getElementById(counter);
      if (createdNode && createdNode.length > 0) {
        const createdMMRef = createdNode.data('MMRef') as MMNode | null;
        if (createdMMRef) {
          createdMMRef.codeName = uniqueCodeName;
          createdNode.data('codeName', uniqueCodeName);
        }
      }
    },
    []
  );

  /**
   * 根据节点拖拽后的落点，同步 Cytoscape compound 包裹关系与 master model 父子关系。
   * 仅处理对象/过程在 InZoom 子图中的进出包裹场景：
   * - 落入 InZoom 根节点范围内 => 成为其子节点
   * - 落在子图空白区域 => 恢复为当前图层顶层节点
   */
  const syncNodeParentAfterDrag = useCallback((draggedNode: NodeSingular) => {
    if (!cy || !currentDiagram.current || !draggedNode || draggedNode.empty()) {
      return;
    }

    const nodeType = draggedNode.data('type');
    if (nodeType !== NodeType.Object && nodeType !== NodeType.Process) {
      return;
    }

    const mmRef = draggedNode.data('MMRef') as MMNode | null;
    if (!mmRef) return;

    if (
      currentDiagram.current.mainNode &&
      mmRef === currentDiagram.current.mainNode
    ) {
      return;
    }

    // 使用节点自身 bbox 中心点作为命中点，统一和父节点 bbox 用同一套坐标系比较。
    const draggedNodeBoundingBox = draggedNode.boundingBox({
      includeLabels: false,
    });
    const position = {
      x: (draggedNodeBoundingBox.x1 + draggedNodeBoundingBox.x2) / 2,
      y: (draggedNodeBoundingBox.y1 + draggedNodeBoundingBox.y2) / 2,
    };
    const excludedNodeIds = new Set(
      draggedNode.descendants().map((item: NodeSingular) => item.id())
    );
    excludedNodeIds.add(draggedNode.id());

    let targetParentNode: NodeSingular | null = null;
    cy.nodes().forEach((node: NodeSingular) => {
      if (!node.isParent()) return;
      if (node.data('display') === 'none') return;
      if (node.data('type') === 'state') return;
      if (excludedNodeIds.has(node.id())) return;
      const childGroupType = node.data('childGroupType') as ChildGroupType;
      if (childGroupType !== ChildGroupType.InZoom) return;

      const bb = node.boundingBox({ includeLabels: false });
      if (
        position.x >= bb.x1 &&
        position.x <= bb.x2 &&
        position.y >= bb.y1 &&
        position.y <= bb.y2
      ) {
        if (
          !targetParentNode ||
          bb.w * bb.h <
            targetParentNode.boundingBox({ includeLabels: false }).w *
              targetParentNode.boundingBox({ includeLabels: false }).h
        ) {
          targetParentNode = node;
        }
      }
    });

    const currentCyParentId = draggedNode.data('parent') || '';
    const currentParentMM = mmRef.parent as MMNode | MMRoot | null;

    if (!currentCyParentId) {
      return;
    }

    const nextParentId =
      targetParentNode && targetParentNode.id() === currentCyParentId
        ? currentCyParentId
        : '';
    const nextParentMM =
      nextParentId && targetParentNode
        ? (targetParentNode.data('MMRef') as MMNode)
        : resolveDiagramTopLevelParent(currentDiagram.current.mainNode);

    if (
      currentParentMM === nextParentMM &&
      currentCyParentId === nextParentId
    ) {
      return;
    }

    currentParentMM?.removeChild(mmRef);
    nextParentMM.addChild(mmRef);
    mmRef.isSubelementOfMain = !!nextParentId;

    recalculateNamespacesForMasterModel();

    if (nextParentId) {
      draggedNode.move({ parent: nextParentId });
    } else {
      draggedNode.move({ parent: null });
    }

    draggedNode.data('parent', nextParentId || undefined);
    clearLabelBBoxCache(draggedNode.id());
    draggedNode.connectedEdges().updateStyle();
    updateFromMasterModel();
  }, []);

  const createNodeAtViewportCenter = useCallback(
    (nodeType: 'object' | 'process') => {
      if (!cy) return;
      const viewport = cy.extent();
      const center = {
        x: (viewport.x1 + viewport.x2) / 2,
        y: (viewport.y1 + viewport.y2) / 2,
      };
      createNodeAtPosition(nodeType, center);
    },
    [createNodeAtPosition]
  );

  const convertClientToCanvasPosition = useCallback((event: DragEvent) => {
    if (!cy) return { x: 0, y: 0 };
    const rect = cy.container().getBoundingClientRect();
    const zoom = cy.zoom();
    const pan = cy.pan();
    return {
      x: (event.clientX - rect.left - pan.x) / zoom,
      y: (event.clientY - rect.top - pan.y) / zoom,
    };
  }, []);

  const handleZoom = useCallback((direction: 'in' | 'out') => {
    if (!cy) return;
    const factor = direction === 'in' ? 1.2 : 0.8;
    const nextZoom = Math.max(0.2, Math.min(4, cy.zoom() * factor));
    cy.zoom({
      level: nextZoom,
      renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
    });
  }, []);

  const handleZoomIn = useCallback(() => handleZoom('in'), [handleZoom]);
  const handleZoomOut = useCallback(() => handleZoom('out'), [handleZoom]);

  const handleResetView = useCallback(() => {
    if (!cy) return;
    smartFitView(cy, 80);
  }, []);

  const handleNodeDragStart = useCallback(
    (event: React.DragEvent<HTMLButtonElement>, nodeType: { type: string }) => {
      if (!event.dataTransfer) return;
      event.dataTransfer.setData(
        'application/json',
        JSON.stringify({ type: nodeType.type })
      );
      event.dataTransfer.effectAllowed = 'copy';
    },
    []
  );

  const handleNodeDragEnd = useCallback(() => {
    // no-op hook for parity with OPMEditor
  }, []);

  // 删除选中元素（节点及其边）
  const handleDeleteSelected = useCallback(async () => {
    if (!cy) return;
    // 兼容框选：box-selected 也视为待删除
    const selected = cy.$(':selected').union(cy.$('.box-selected'));
    if (selected.length === 0) return;

    // 如果删除了当前选中节点，关闭外部编辑面板并清理内部选中状态
    const selectedNodeIds = selected.nodes().map(n => n.id());
    const isDeletingCurrent =
      selectedNodeRef.current &&
      selectedNodeIds.includes(selectedNodeRef.current.id());

    const localRemovedNodeIds = new Set<string>();
    const currentSubdiagramRootNode = selected
      .nodes()
      .toArray()
      .find(node => isCurrentSubdiagramRootNode(node));
    if (currentSubdiagramRootNode) {
      const confirmed = await confirmDeleteSubdiagramRootNode();
      if (!confirmed) return;
      if (deleteCurrentSubdiagramByRootNode(currentSubdiagramRootNode)) {
        setBoxSelectedNodes([]);
        setBoxSelectionBounds(null);
        cy.$('.box-selected').removeClass('box-selected');
        setClearSelectionTrigger(t => t + 1);
        return;
      }
    }

    // 先处理节点：多图层级联节点仅从当前图层移除。
    for (const node of selected.nodes().toArray()) {
      if (node.removed()) {
        continue;
      }
      const appearanceCount = countNodeDiagramAppearances(node.id());
      const nodeMMRef = node.data('MMRef') as MMNode | null;
      const shouldCascadeDelete = shouldCascadeDeleteStateValueNode(nodeMMRef);
      const shouldTreatAsDiagramRoot = shouldTreatAsDiagramRootNode(nodeMMRef);
      if (hasActiveChildNodes(node)) {
        const confirmed = await confirmDeleteNodeWithChildren();
        if (!confirmed) return;
      }
      if (
        appearanceCount > 1 &&
        nodeMMRef &&
        !shouldCascadeDelete &&
        !shouldTreatAsDiagramRoot
      ) {
        const removedIdSet = removeNodeInCurrentDiagramOnly(node);
        removedIdSet.forEach(id => localRemovedNodeIds.add(String(id)));
        continue;
      }
      removeNodeContextMenu(node, dispatch, currentDiagram.current);
      node.remove();
    }

    // 再处理边：与“仅当前图层移除节点”关联的边，不做全局删除。
    selected.edges().forEach(edge => {
      if (edge.removed()) {
        return;
      }
      const sourceId = edge.data('source');
      const targetId = edge.data('target');
      if (
        localRemovedNodeIds.has(String(sourceId)) ||
        localRemovedNodeIds.has(String(targetId))
      ) {
        return;
      }

      // 子图中若边在其他图层也存在（历史共享边），删除时仅移除当前图层。
      if (
        currentDiagram.current.mainNode instanceof MMNode &&
        isEdgeSharedWithOtherDiagrams(edge.id())
      ) {
        edge.remove();
        return;
      }

      removeEdgeContextMenu(edge);
      edge.remove();
    });

    if (isDeletingCurrent) {
      setSelectedNode(null);
      setIsLabelSelected(false);
      onNodeSelect?.(null);
    }

    // 清除框选状态与描边
    setBoxSelectedNodes([]);
    setBoxSelectionBounds(null);
    cy.$('.box-selected').removeClass('box-selected');
    setClearSelectionTrigger(t => t + 1);
  }, [
    dispatch,
    countNodeDiagramAppearances,
    shouldCascadeDeleteStateValueNode,
    isCurrentSubdiagramRootNode,
    shouldTreatAsDiagramRootNode,
    deleteCurrentSubdiagramByRootNode,
    confirmDeleteNodeWithChildren,
    confirmDeleteSubdiagramRootNode,
    hasActiveChildNodes,
    removeNodeInCurrentDiagramOnly,
    isEdgeSharedWithOtherDiagrams,
    onNodeSelect,
  ]);

  // 复制选中的节点
  const handleCopy = useCallback(() => {
    if (!cy) return;

    // 获取当前框选的节点（优先使用 React state 中的框选节点）
    let selectedNodes: any;
    if (boxSelectedNodesRef.current && boxSelectedNodesRef.current.length > 0) {
      // 使用 React state 中记录的框选节点，避免累加问题
      selectedNodes = cy.collection();
      boxSelectedNodesRef.current.forEach(node => {
        if (!node.removed()) {
          selectedNodes = selectedNodes.union(node);
        }
      });
    } else {
      // 没有框选，使用 cytoscape 的选中状态
      selectedNodes = cy.$(':selected').nodes();
    }

    if (selectedNodes.length === 0) return;

    // 过滤掉单独选中的状态节点（状态节点只能跟随父节点复制）
    const nodesToCopy: NodeSingular[] = [];
    const nodeIds = new Set<string>();

    selectedNodes.forEach((node: NodeSingular) => {
      const type = node.data('type');
      // 如果是状态节点，检查其父节点是否也被选中
      if (type === 'state') {
        const parentId = node.data('parent');
        if (parentId && selectedNodes.getElementById(parentId).length > 0) {
          // 父节点被选中，状态节点会跟随父节点一起复制
          nodesToCopy.push(node);
          nodeIds.add(node.id());
        }
        // 单独选中的状态节点不复制
      } else {
        nodesToCopy.push(node);
        nodeIds.add(node.id());
      }
    });

    // 收集所有子节点（递归）
    const collectChildren = (node: NodeSingular) => {
      const children = node.children();
      children.forEach((child: NodeSingular) => {
        if (!nodeIds.has(child.id())) {
          nodesToCopy.push(child);
          nodeIds.add(child.id());
          collectChildren(child);
        }
      });
    };
    nodesToCopy.slice().forEach(node => collectChildren(node));

    if (nodesToCopy.length === 0) return;

    // 计算选中节点的中心点
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    nodesToCopy.forEach(node => {
      const bb = node.boundingBox({ includeLabels: false });
      minX = Math.min(minX, bb.x1);
      minY = Math.min(minY, bb.y1);
      maxX = Math.max(maxX, bb.x2);
      maxY = Math.max(maxY, bb.y2);
    });
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    // 收集节点数据（排除 MMRef 避免循环引用）
    const clipboardNodes: ClipboardNode[] = nodesToCopy.map(node => {
      const nodeData = node.data();
      // 只保留需要的属性，排除 MMRef（包含循环引用）
      const safeData = {
        id: nodeData.id,
        label: nodeData.label,
        type: nodeData.type,
        parent: nodeData.parent,
        customWidth: nodeData.customWidth,
        customHeight: nodeData.customHeight,
        essence: nodeData.MMRef?.essence,
        affiliation: nodeData.MMRef?.affiliation,
        codeName: nodeData.MMRef?.codeName,
        description: nodeData.MMRef?.description,
        tags: nodeData.MMRef?.tags,
        objectType: nodeData.MMRef?.objectType,
        exampleValues: nodeData.MMRef?.exampleValues,
        version: nodeData.MMRef?.version,
        updatedAt: nodeData.MMRef?.updatedAt,
        updatedBy: nodeData.MMRef?.updatedBy,
        relatedSystem: nodeData.MMRef?.relatedSystem,
      };
      return {
        id: node.id(),
        label: node.data('label'),
        type: node.data('type'),
        parent: node.data('parent') || '',
        position: { x: node.position().x, y: node.position().y },
        data: safeData,
      };
    });

    // 收集节点之间的边（排除 MMRef 避免循环引用）
    const clipboardEdges: ClipboardEdge[] = [];
    cy.edges().forEach((edge: any) => {
      const sourceId = edge.data('source');
      const targetId = edge.data('target');
      if (nodeIds.has(sourceId) && nodeIds.has(targetId)) {
        const edgeData = edge.data();
        const safeEdgeData = {
          id: edgeData.id,
          label: edgeData.label,
          type: edgeData.type,
          source: edgeData.source,
          target: edgeData.target,
          linkQualifier: edgeData.linkQualifier || LinkQualifier.None,
          linkNegation: edgeData.linkNegation || LinkNegation.None,
          markerColor: edgeData.markerColor || '#586D8C',
        };
        clipboardEdges.push({
          id: edge.id(),
          label: edge.data('label') || '',
          type: edge.data('type') || '',
          source: sourceId,
          target: targetId,
          data: safeEdgeData,
          linkQualifier: safeEdgeData.linkQualifier,
          linkNegation: safeEdgeData.linkNegation,
          markerColor: safeEdgeData.markerColor,
        });
      }
    });

    // 保存到剪贴板（完全替换，不累加）
    clipboard = {
      nodes: clipboardNodes,
      edges: clipboardEdges,
      centerX,
      centerY,
    };

    console.log(
      `已复制 ${clipboardNodes.length} 个节点和 ${clipboardEdges.length} 条边`
    );
  }, []);

  // 粘贴节点
  const handlePaste = useCallback(() => {
    if (!cy || !clipboard || clipboard.nodes.length === 0) return;

    // 生成新旧ID映射
    const idMap = new Map<string, string>();
    clipboard.nodes.forEach(node => {
      const newId = String(eleCounter.value);
      idMap.set(node.id, newId);
    });

    // 计算粘贴位置偏移（相对于视口中心，或者原位置偏移一定距离）
    const viewport = cy.extent();
    const viewCenterX = (viewport.x1 + viewport.x2) / 2;
    const viewCenterY = (viewport.y1 + viewport.y2) / 2;
    // 偏移量：向右下偏移50像素避免重叠
    const offsetX = viewCenterX - clipboard.centerX + 50;
    const offsetY = viewCenterY - clipboard.centerY + 50;

    // 获取当前图表的主节点作为 Master Model 父节点
    const parentMMNode = resolveDiagramTopLevelParent(
      currentDiagram.current?.mainNode
    ) as MMNode | MMRoot;

    // 先按层级排序节点（父节点在前）
    const sortedNodes = [...clipboard.nodes].sort((a, b) => {
      // 没有父节点的在前
      if (!a.parent && b.parent) return -1;
      if (a.parent && !b.parent) return 1;
      // 父节点在被复制列表中的，子节点排在后面
      const aParentInList = clipboard!.nodes.some(n => n.id === a.parent);
      const bParentInList = clipboard!.nodes.some(n => n.id === b.parent);
      if (!aParentInList && bParentInList) return -1;
      if (aParentInList && !bParentInList) return 1;
      return 0;
    });

    // 复制粘贴时执行“全图层全局唯一”重命名：
    // - 名称：按后缀数字递增；
    // - 英文名称：优先用“名称 + en”，冲突后再递增。
    const usedLabels = buildUsedNodeLabelSet();
    const usedCodeNames = buildUsedNodeCodeNameSet();

    // 创建新节点
    const newNodeIds: string[] = [];
    const createdModelNodes = new Map<string, MMNode>();
    sortedNodes.forEach(node => {
      const newId = idMap.get(node.id)!;
      newNodeIds.push(newId);

      const sourceLabel = (node.label || node.type || 'Node').trim();
      const uniqueLabel = generateUniqueSequencedValue(
        sourceLabel,
        usedLabels,
        sourceLabel || 'Node'
      );
      usedLabels.add(uniqueLabel);

      const isObjectOrProcess =
        node.type === NodeType.Object || node.type === NodeType.Process;
      const uniqueCodeName = isObjectOrProcess
        ? generateUniqueCodeNameByLabel(uniqueLabel, usedCodeNames)
        : '';
      if (uniqueCodeName) {
        usedCodeNames.add(uniqueCodeName);
      }

      // 确定新的父节点ID
      let newParent = '';
      if (node.parent && idMap.has(node.parent)) {
        // 父节点也在复制列表中，使用映射后的新ID
        newParent = idMap.get(node.parent)!;
      }
      // 如果父节点不在复制列表中，则不设置父节点（成为顶层节点）

      const newPosition = {
        x: node.position.x + offsetX,
        y: node.position.y + offsetY,
      };

      // 创建新的 MMNode
      const modelNode = new MMNode(newId, node.type as NodeType, uniqueLabel);
      const modelParentNode =
        (newParent ? createdModelNodes.get(newParent) : null) || parentMMNode;
      modelParentNode.addChild(modelNode);
      createdModelNodes.set(newId, modelNode);

      // 复制原节点的属性到新 MMNode
      if (node.data.essence) modelNode.essence = node.data.essence;
      if (node.data.affiliation) modelNode.affiliation = node.data.affiliation;
      if (node.data.description) modelNode.description = node.data.description;
      modelNode.codeName = uniqueCodeName;
      if (Array.isArray(node.data.tags)) modelNode.tags = node.data.tags;
      if (node.data.objectType) modelNode.objectType = node.data.objectType;
      if (node.data.exampleValues)
        modelNode.exampleValues = node.data.exampleValues;
      if (node.data.version) modelNode.version = node.data.version;
      if (node.data.updatedAt) modelNode.updatedAt = node.data.updatedAt;
      if (Array.isArray(node.data.updatedBy))
        modelNode.updatedBy = node.data.updatedBy;
      if (node.data.relatedSystem)
        modelNode.relatedSystem = node.data.relatedSystem;

      if (newParent && node.type !== 'state') {
        modelNode.isSubelementOfMain = true;
      } else if (node.type === 'state') {
        modelNode.isStructurePart = true;
      }

      recalculateNamespacesForMasterModel();

      // 添加节点到 cytoscape
      cy.add({
        data: {
          id: newId,
          label: uniqueLabel,
          type: node.type,
          parent: newParent,
          MMRef: modelNode,
          codeName: uniqueCodeName,
          namespace: modelNode.namespace || '',
          packageName: modelNode.packageName || '',
          customWidth: node.data.customWidth,
          customHeight: node.data.customHeight,
        },
        position: newPosition,
      });

      const addedNode = cy.getElementById(newId);
      applyAutoLabelWidth(addedNode, uniqueLabel);
    });

    // 创建新边（需要创建 MMEdge 并添加到 originalEdgeArray，否则导出时会丢失）
    clipboard.edges.forEach(edge => {
      const newSourceId = idMap.get(edge.source);
      const newTargetId = idMap.get(edge.target);
      if (!newSourceId || !newTargetId) return;

      const newEdgeId = `e${eleCounter.value}`;

      // 获取新创建的节点的 MMRef
      const sourceNode = cy.getElementById(newSourceId);
      const targetNode = cy.getElementById(newTargetId);
      if (sourceNode.empty() || targetNode.empty()) return;

      const sourceMMRef = sourceNode.data('MMRef') as MMNode;
      const targetMMRef = targetNode.data('MMRef') as MMNode;
      if (!sourceMMRef || !targetMMRef) return;

      // 创建 MMEdge 并添加到 originalEdgeArray
      const mmEdge = new MMEdge(
        newEdgeId,
        sourceMMRef,
        targetMMRef,
        edge.type as EdgeType,
        false, // propagation
        undefined,
        edge.label || '',
        edge.linkQualifier || LinkQualifier.None,
        edge.linkNegation || LinkNegation.None
      );
      originalEdgeArray.addEdge(mmEdge);

      cy.add({
        data: {
          id: newEdgeId,
          label: edge.label,
          type: edge.type,
          source: newSourceId,
          target: newTargetId,
          MMRef: mmEdge, // 关键：设置 MMRef 以便导出时能正确处理
          linkQualifier: edge.linkQualifier || LinkQualifier.None,
          linkNegation: edge.linkNegation || LinkNegation.None,
          markerLabel: buildMarkerLabel(
            edge.linkQualifier || LinkQualifier.None,
            edge.linkNegation || LinkNegation.None
          ),
          markerColor: edge.markerColor || '#586D8C',
        },
      });
    });

    // 清除之前的所有选中状态
    cy.elements().unselect();
    cy.$('.box-selected').removeClass('box-selected');
    setSelectedNode(null);
    setIsLabelSelected(false);
    updateFromMasterModel();
    setBoxSelectedNodes([]);
    setBoxSelectionBounds(null);

    // 收集新粘贴的节点
    const pastedNodes = newNodeIds
      .map(id => cy.getElementById(id))
      .filter(n => !n.empty()) as NodeSingular[];

    if (pastedNodes.length > 0) {
      // 只给新节点添加 box-selected 类（不使用 cytoscape 的 select，避免样式冲突）
      pastedNodes.forEach(node => {
        node.addClass('box-selected');
      });

      // 计算新的框选边界
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      pastedNodes.forEach(node => {
        const bb = node.boundingBox({ includeLabels: false });
        minX = Math.min(minX, bb.x1);
        minY = Math.min(minY, bb.y1);
        maxX = Math.max(maxX, bb.x2);
        maxY = Math.max(maxY, bb.y2);
      });

      // 更新 React state
      setBoxSelectedNodes(pastedNodes);
      setBoxSelectionBounds({ x1: minX, y1: minY, x2: maxX, y2: maxY });
      setInteractionMode(InteractionMode.BoxSelecting);
    }

    console.log(`已粘贴 ${pastedNodes.length} 个节点`);
  }, []);

  useEffect(() => {
    if (!cyContainerRef.current) return;
    const instance = cytoscape({
      container: cyContainerRef.current,
      style: cyStylesheet,
      wheelSensitivity: 0.1,
    });
    registerCustomArrowShapes(instance);
    instance.style().fromJson(cyStylesheet).update();
    // 注册新的交互事件（替代原来的右键菜单）
    registerInteractionHandlers(instance);
    registerEdgeEventHandlers(instance);
    registerMarkerLabelHandlers(instance);
    registerPopperHandlers(instance);
    instance.edgeEditing(eeDefaults);
    onRenderStart?.();
    const finishInitialRender = () =>
      requestAnimationFrame(() => onRenderEnd?.());
    instance.one('render', finishInitialRender);

    // 同时设置模块级变量和 state
    // 模块级变量供外部文件使用，state 触发重新渲染让 BoxSelection 能正确绑定事件
    cy = instance;
    setCyInstance(instance);

    return () => {
      instance.destroy();
      cy = null;
      setCyInstance(null);
    };
  }, []);

  // 禁用 OPMCV 区域内浏览器系统右键菜单（避免节点/覆盖层元素触发系统菜单）
  useEffect(() => {
    const container = diagramContainerRef.current;
    if (!container) return;

    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const tag = target?.tagName;
      // 表单输入区域保留浏览器默认右键菜单（复制/粘贴等）
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        target?.isContentEditable
      ) {
        return;
      }
      e.preventDefault();
    };

    // 使用捕获阶段，确保即使内部元素 stopPropagation 也能拦截到
    container.addEventListener('contextmenu', handleContextMenu, true);
    return () => {
      container.removeEventListener('contextmenu', handleContextMenu, true);
    };
  }, []);

  useEffect(() => {
    const container = diagramContainerRef.current;
    if (!container) return;
    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    };
    const handleDrop = (event: DragEvent) => {
      event.preventDefault();
      const payload = event.dataTransfer?.getData('application/json');
      if (!payload) return;
      try {
        const parsed = JSON.parse(payload);
        if (parsed.type !== 'object' && parsed.type !== 'process') return;
        const position = convertClientToCanvasPosition(event);
        createNodeAtPosition(parsed.type, position);
      } catch (err) {
        console.error('Failed to parse dropped node payload', err);
      }
    };
    container.addEventListener('dragover', handleDragOver);
    container.addEventListener('drop', handleDrop);
    return () => {
      container.removeEventListener('dragover', handleDragOver);
      container.removeEventListener('drop', handleDrop);
    };
  }, [convertClientToCanvasPosition, createNodeAtPosition]);

  // 监听键盘快捷键（删除、复制、粘贴）
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const tag = target?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        target?.isContentEditable
      ) {
        return;
      }

      // 删除
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        handleDeleteSelected();
        return;
      }

      // 复制 Ctrl+C / Cmd+C
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        handleCopy();
        return;
      }

      // 粘贴 Ctrl+V / Cmd+V
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        handlePaste();
        return;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleDeleteSelected, handleCopy, handlePaste]);

  // 框选完成回调 - 接收节点和边界信息
  const handleBoxSelectionComplete = useCallback(
    (
      nodes: NodeSingular[],
      bounds: { x1: number; y1: number; x2: number; y2: number } | null
    ) => {
      setBoxSelectedNodes(nodes);
      setBoxSelectionBounds(bounds);
      setInteractionMode(InteractionMode.BoxSelecting);
    },
    []
  );

  // 框选清除回调
  const handleBoxSelectionClear = useCallback(() => {
    setBoxSelectedNodes([]);
    setBoxSelectionBounds(null);
    setInteractionMode(InteractionMode.None);
  }, []);

  // 缩放完成回调
  const handleResizeEnd = useCallback(
    (node: NodeSingular, width: number, height: number) => {
      // 尺寸已在 ResizeHandles 组件中保存到 MMNode
      console.log(`节点 ${node.id()} 尺寸调整完成: ${width}x${height}`);
    },
    []
  );

  return (
    <div className="diagram-canvas-container" ref={diagramContainerRef}>
      <div className="diagram-canvas" id="cy" ref={cyContainerRef} />

      {parentChildInvocationOverlayLines.length > 0 && (
        <svg style={parentChildInvocationOverlayStyle}>
          <defs>
            <marker
              id={PARENT_CHILD_INVOCATION_OVERLAY_MARKER_ID}
              markerWidth="30"
              markerHeight="24"
              refX="22"
              refY="12"
              orient="auto"
              markerUnits="userSpaceOnUse"
            >
              <path
                d={getInfluenceArrowMarkerPath()}
                fill="#fff"
                stroke="context-stroke"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </marker>
          </defs>
          {parentChildInvocationOverlayLines.map(line => (
            <g key={line.id}>
              <path
                d={line.d}
                fill="none"
                stroke="transparent"
                strokeWidth={Math.max(14, line.strokeWidth + 10)}
                strokeLinecap="round"
                strokeLinejoin="round"
                pointerEvents="stroke"
                onMouseDown={evt => {
                  evt.preventDefault();
                  evt.stopPropagation();
                }}
                onClick={evt => {
                  evt.preventDefault();
                  evt.stopPropagation();
                  selectEdgeFromOverlay(line.id);
                }}
              />
              <path
                d={line.d}
                fill="none"
                stroke={line.stroke}
                strokeWidth={line.strokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                markerEnd={`url(#${PARENT_CHILD_INVOCATION_OVERLAY_MARKER_ID})`}
              />
            </g>
          ))}
          {selectedEdgeReadonlyHandles.map(handle => (
            <g key={`selected-edge-handle-${handle.id}`}>
              <circle
                cx={handle.source.x}
                cy={handle.source.y}
                r={EDGE_SELECTION_DOT_RADIUS}
                fill="#000"
              />
              <circle
                cx={handle.target.x}
                cy={handle.target.y}
                r={EDGE_SELECTION_DOT_RADIUS}
                fill="#000"
              />
            </g>
          ))}
        </svg>
      )}

      {/* 缩放控制点 - 仅在通过点击文字选中节点且节点有效时显示 */}
      {isLabelSelected && isSelectedNodeValid && selectedNode && (
        <ResizeHandles
          cy={cy}
          selectedNode={selectedNode}
          onResizeEnd={handleResizeEnd}
        />
      )}

      {/* 节点操作菜单 - 仅在通过点击文字选中节点且节点有效时显示 */}
      {isLabelSelected &&
        isSelectedNodeValid &&
        selectedNode &&
        !showEdgeEditDialog &&
        !isCurrentSubdiagramRootNode(selectedNode) && (
          <NodeActionMenu
            cy={cy}
            selectedNode={selectedNode}
            menuItems={menuItems}
          />
        )}

      <ExecutionOverlay
        cy={cyInstance || cy || null}
        snapshot={simulationSnapshot || null}
      />

      <EdgeEditDialog
        open={showEdgeEditDialog}
        mode={edgeDialogMode}
        edge={editingEdge}
        edgeType={editingEdgeType}
        edgeLabel={editingEdgeLabel}
        linkQualifier={editingEdgeQualifier}
        linkNegation={editingEdgeNegation}
        directionInfo={edgeDirection}
        onEdgeTypeChange={handleEdgeTypeChange}
        onEdgeLabelChange={setEditingEdgeLabel}
        onLinkQualifierChange={setEditingEdgeQualifier}
        onLinkNegationChange={setEditingEdgeNegation}
        onConfirm={handleEdgeEditConfirm}
        onCancel={handleEdgeEditCancel}
      />

      <ImportDataDialog
        visible={showImportDataDialog}
        nodeId={importingNode?.id() || null}
        nodeName={
          importingNode?.data('displayLabel') ||
          importingNode?.data('label') ||
          importingNode?.id()
        }
        onConfirm={handleImportDataConfirm}
        onCancel={handleCloseImportDataDialog}
      />

      <ProcessFunctionDialog
        visible={showFunctionDialog}
        node={functionNode}
        ontologyId={ontologyId}
        onClose={() => {
          setShowFunctionDialog(false);
          setFunctionNode(null);
        }}
      />

      {/* 框选组件 - 使用 cyInstance 确保初始化后触发重新渲染 */}
      <BoxSelection
        cy={cyInstance}
        onSelectionComplete={handleBoxSelectionComplete}
        onSelectionClear={handleBoxSelectionClear}
        wholeNode={false}
        clearTrigger={clearSelectionTrigger}
        externalSelectedNodes={boxSelectedNodes}
        externalBounds={boxSelectionBounds}
      />

      <FloatingToolbar
        onAddNode={createNodeAtViewportCenter}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetView={handleResetView}
        onNodeDragStart={handleNodeDragStart}
        onNodeDragEnd={handleNodeDragEnd}
      />
    </div>
  );
};

export default DiagramCanvas;
export { cy };
